import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, finalize, map, of, retry, timeout } from 'rxjs';
import { LocationRegisterList } from 'src/app/_data/location-resgister';
import { UsStates } from 'src/app/_data/us-states';
import { CouponValidationRequest, RemoveCouponRequest } from 'src/app/_models/discount';
import { SubscriptionDiscount } from 'src/app/_models/coupon/validate-coupon-response';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { DiscountsService } from 'src/app/_services/discounts.service';
import { GlobalService } from 'src/app/_services/global.service';
import { PlanDataService } from 'src/app/_services/plan-data.service';
import { SignupService } from 'src/app/_services/signup.service';
import { SubscriptionPlanService } from 'src/app/_services/subscription-plan.service';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { UserService } from 'src/app/_services/user.service';
import { StripeCardSavedEvent } from 'src/app/_services/stripe-card.service';
import { LocationRegisterType } from 'src/app/types/location-register';
import { environment } from 'src/environments/environment';
import { ComponentCanDeactivate } from 'src/app/_guards/unsaved-changes.guard';
import {
    buildRecurringSubscriptionTotals,
    buildUsageReceiptTotals,
    readInvoiceRecurringSubtotal,
    readAvailableBusinessSetup,
    readOwnedRecurringQuantity,
    readSubscriptionRecurringSubtotal,
} from './subscription-total';

declare const Stripe;

/** Standalone "Checkout & cart" page (LincSell Signup.dc.html prototype), reached only from
 *  change-plan/billing-and-plans's goToCheckOut() for a free-trial account picking its first
 *  paid plan. Submission still goes through the same saveUserSignupInfo -> saveUserSignupInfoForStripe
 *  -> Stripe Checkout redirect this page always used — only the UI and the line items that feed
 *  that payload (registers/locations, add-ons, hardware, coupon) are new. */
@Component({
    selector: 'app-payment-page',
    templateUrl: './payment-page.component.html',
    styleUrls: ['./payment-page.component.scss'],
})
export class PaymentPageComponent implements OnInit, ComponentCanDeactivate {

    _global = GlobalService;

    isLoading = true;
    // Gate isLoading on BOTH resolvePlan() and refreshUserInfo() finishing — refreshUserInfo()
    // fires without blocking resolvePlan() (see its call site's doc comment), so relying on
    // resolvePlan() alone to clear isLoading could reveal the summary with a stale cached
    // userInfo (e.g. an outdated isFreeTrial flag), then have grandTotal jump to a different
    // value moments later once the fresh userInfo lands — a "why did the price just change"
    // flash that reads as the page lying about the charge. isUserInfoRefreshed defaults to true
    // and is only held back while an actual refresh is in flight (see refreshUserInfo()).
    isUserInfoRefreshed = true;
    private isInvoicePreviewLoaded = false;
    plan: any = null;
    billingInterval: 'Monthly' | 'Yearly' = 'Monthly';
    // True when reached via billing-and-plans's "Buy more"/"Add" (a usage-intent add-on to the
    // current plan) rather than picking a plan on /change-plan — "Change plan" would let the
    // merchant swap plans mid-purchase, which isn't what this trip is for.
    isUsageOnlyCheckout = false;
    // True only for a hardware-shop.component.ts "Proceed to checkout" trip — hides the
    // registers/locations/add-ons sections that don't apply when the merchant only came here to
    // buy hardware. Distinct from isUsageOnlyCheckout, which also covers plain register/location/
    // add-on "Buy more" trips where hardware stays hidden instead.
    hardwareOnlyCheckout = false;

    registersAndLocationsList: LocationRegisterType[] = [];
    // Per-plan purchase ceilings — null means "no cap fetched yet / plan has no limit for this
    // resource (unlimited)". Populated from the live plan catalog once resolvePlan() runs; see
    // loadUsageLimits() for where these come from.
    maxRegisters: number | null = null;
    maxLocations: number | null = null;
    // Whether the SELECTED plan (this.plan — the one being switched to, not necessarily the
    // account's current plan) allows buying extra registers/locations at all. null = not loaded
    // yet, don't block. Same live Locations/Registers featureData addMore/unlimited check
    // billing-and-plans.component.ts's canBuyMoreUsage uses — see loadUsageLimits().
    canBuyExtraRegisters: boolean | null = null;
    canBuyExtraLocations: boolean | null = null;
    // True while loadUsageLimits() is in flight — both the Add-ons list and the Registers &
    // locations steppers' purchase eligibility/caps start out from the local catalog and silently
    // swap to live data once this resolves, which otherwise looks like a jarring pop-in of new
    // items (add-ons) or a button that suddenly disables (steppers) with no warning.
    isLoadingUsageLimits = true;
    currentInvoice: any = null;

    hardwareSupported = false;
    hardwaresList: any[] = [];
    /** Same items as hardwaresList, grouped for display in the hardware picker dialog — one
     *  section per GetHardwarePackagesv2 category, mirroring hardware-shop.component.ts's
     *  hardwareCategories. Items are the same object references as hardwaresList, so toggling
     *  quantity here updates the exact objects selectedHardware/persistCartState already read. */
    hardwareCategories: { title: string; items: any[] }[] = [];
    loadingHardwares = false;
    hardwareDialogVisible = false;

    couponCode = '';
    couponSuccess = false;
    couponError = false;
    isCouponValidating = false;
    validationMessage = '';
    appliedCouponCode: string | null = null;
    discountedPrice = 0;
    discountAmount = 0;
    lastCouponCategory: 'plan' | 'subscription' | 'addon' | null = null;
    stripeCouponId = '';
    stripeProductKey = '';
    // Full discount record from validateCoupon's response — captured so confirm() can build
    // DiscountDet for the backend; CouponCode alone doesn't apply anything server-side (per
    // v3/subscription/update's contract, DiscountDet must be paired with it).
    appliedDiscountDetail: SubscriptionDiscount | null = null;

    terms = false;
    isSubmitting = false;

    // --- Shipping address (hardware orders only) ---
    // Defaults to the business's own address; the merchant can uncheck and enter a different
    // shipping address, matching signup-v4's checkout — the only place hardware physically ships to
    // someone, so it's the only place this page needs an address beyond the business profile's own.
    isBusinessAddressShipping = true;
    shippingForm: FormGroup;
    usaStates = UsStates;
    selectedShippingState: any = UsStates[0];

    // --- Business address (only shown/required when the account has none on file yet) ---
    // Hardware's tax calculation is based on the business's state, so a merchant who never
    // completed this during signup/business setup needs to provide it here before checking out —
    // shippingForm above still covers where the hardware itself ships, which is a separate concern.
    businessAddressForm: FormGroup;
    selectedBusinessState: any = UsStates[0];

    // --- Usage-only checkout post-submit states (Buy more/Add) ---
    // Registers/locations/add-ons always defer to the account's card-on-file (trial-end billing or
    // the existing recurring subscription) — only hardware is ever an immediate Stripe charge, since
    // it's a one-time physical purchase. showAddCardStep/submitSucceeded gate which of the three
    // post-submit views renders; see confirm()/completeUsageOnlyPurchase() for the branching.
    showAddCardStep = false;
    submitSucceeded = false;
    purchasedLineItems: any[] = [];
    // The full confirm() payload, kept around so onAddCardStepSaved() can re-trigger
    // updateSubscriptionV3 with the same business/plan identifiers once a card is on file — see
    // that method's doc comment for why.
    private lastConfirmPayload: any = null;

    // Set only when confirm() short-circuited straight to showAddCardStep BEFORE submitOrder() ever
    // ran (see confirm()'s hasCardOnFile pre-check) — there's no lastConfirmPayload yet in that
    // case, so onAddCardStepSaved() can't just retry chargeHardware() directly like it does for the
    // mid-flow (backend-rejected) case. Re-running confirm() from scratch instead is safe: canSubmit
    // is rechecked and hasCardOnFile is now true, so it proceeds straight through submitOrder().
    awaitingCardForPrecheck = false;

    // --- Leave-with-unsaved-changes confirm (Back button, browser Back/Forward) ---
    showLeaveConfirm = false;
    private pendingLeaveResolve: ((leave: boolean) => void) | null = null;
    // Set only while the leave-confirm dialog was opened by the topbar X button (requestExit())
    // rather than the router's canDeactivate() guard — confirmLeave() branches on this so a manual
    // X click navigates directly instead of trying to resolve a guard promise that doesn't exist.
    private exitRequestedManually = false;

    constructor(
        public _signup: SignupService,
        public _abd: AppBaseDataService,
        private _planData: PlanDataService,
        private _discountService: DiscountsService,
        private _connMessageService: ConnMessageService,
        private _spinner: NgxSpinnerService,
        private _router: Router,
        private _subscriptionPlanService: SubscriptionPlanService,
        private _businessSetupProgress: BusinessSetupProgressService,
        private _fb: FormBuilder,
        private _route: ActivatedRoute,
        private _userService: UserService,
        private _businessSetupService: BusinessSetupService,
    ) { }

    ngOnInit(): void {
        // Browser Back from the just-shown success slip lands back on this same route with a
        // stale/completed cart — redirect straight to Subscription Management instead of letting
        // the merchant re-run (and re-pay for) a checkout that already went through. See
        // markSubmitSucceeded()'s doc comment for where this flag is set/cleared.
        let checkoutLocked = false;
        try { checkoutLocked = sessionStorage.getItem(PaymentPageComponent.CHECKOUT_LOCK_KEY) === 'true'; } catch { /* ignore */ }
        if (checkoutLocked && !this.isUsageOnlyEntry()) {
            this._router.navigate(['/app/billing-and-plans'], { replaceUrl: true });
            return;
        }
        this.initShippingForm();
        // GlobalService.userInfo may be a stale snapshot from earlier in this session (e.g. a
        // previous add-on purchase made this same visit isn't the concern — refreshUserInfo() there
        // already covers it — but simply landing here fresh, or after time has passed, has no
        // guarantee the cached copy reflects the account's real subscribedFeatures). optionalAddons
        // re-evaluates GlobalService.userInfo live on every render (it's a getter, not a cached
        // array), so this refresh alone is enough to correct an already-rendered "purchase it again"
        // list once it resolves — no need to block resolvePlan() on it.
        this.refreshUserInfo();
        this.resolvePlan();
        this.loadCurrentInvoicePreview();
    }

    private initShippingForm(): void {
        this.shippingForm = this._fb.group({
            address: ['', Validators.required],
            address2: [''],
            city: ['', Validators.required],
            zipCode: ['', Validators.required],
        });
        this.businessAddressForm = this._fb.group({
            address: ['', Validators.required],
            city: ['', Validators.required],
            zipCode: ['', Validators.required],
        });
        // Otherwise only captured by persistCartState() calls triggered from stepper/checkbox
        // clicks — a merchant who only types an address and reloads before touching anything
        // else would find the form blank again.
        this.businessAddressForm.valueChanges.subscribe(() => this.persistCartState());
    }

    get sf() {
        return this.shippingForm.controls;
    }

    get baf() {
        return this.businessAddressForm.controls;
    }

    private get currentBusinessSetup(): any {
        let persistedSetup: any = null;
        try {
            persistedSetup = sessionStorage.getItem('business-Info');
        } catch { /* sessionStorage can be unavailable in private browsing */ }
        return readAvailableBusinessSetup(GlobalService.userInfo, persistedSetup);
    }

    /** No business address saved on the account yet — gates the warning + inline form, and blocks
     *  submission until it's filled in, since sales tax can't be calculated without a state. */
    get hasBusinessAddress(): boolean {
        const setup = this.currentBusinessSetup;
        const address = String(setup?.address ?? setup?.Address ?? '').trim();
        const city = String(setup?.city ?? setup?.City ?? '').trim();
        const state = String(setup?.state ?? setup?.State ?? '').trim();
        const zip = String(setup?.zip ?? setup?.Zip ?? '').trim();
        return !!(address && city && state && zip);
    }

    /** One-line rendering of the account's saved business address, shown under "Same as business
     *  address" once it's checked so the merchant can see where hardware will actually ship. */
    get businessAddressOneLine(): string {
        const setup = this.currentBusinessSetup;
        const address = String(setup?.address ?? setup?.Address ?? '').trim();
        const city = String(setup?.city ?? setup?.City ?? '').trim();
        const state = String(setup?.state ?? setup?.State ?? '').trim();
        const zip = String(setup?.zip ?? setup?.Zip ?? '').trim();
        return [address, [city, state].filter(Boolean).join(', '), zip].filter(Boolean).join(', ');
    }

    /** True once the inline business-address form is ready to submit — gates canSubmit so the
     *  main Proceed button isn't stuck disabled just because businessSetup[0] itself has no
     *  address yet; saveBusinessAddressForConfirm() saves this form's contents as the first step
     *  of confirm() rather than through a separate button. */
    get canSaveBusinessAddress(): boolean {
        return this.businessAddressForm?.valid && !!this.selectedBusinessState;
    }

    /** Saves the inline business-address form to the account, same endpoint/payload the old
     *  standalone "Save business address" button used — now folded into confirm() itself (see
     *  its doc comment) instead of requiring its own click before the merchant can proceed.
     *  Resolves true/false rather than throwing so confirm() can branch without its own
     *  try/catch. */
    private saveBusinessAddressForConfirm() {
        const setup = this.currentBusinessSetup;
        if (!setup) return of(false);

        const value = this.businessAddressForm.value;
        // Preserve every field this narrow form doesn't collect so a merchant filling this in from
        // checkout never wipes out business info saved elsewhere — same approach
        // business-setup-checklist.component.ts's saveBusinessInfo() uses for the same endpoint.
        const payload = {
            businessName: setup?.businessDisplayName ?? setup?.businessName ?? setup?.BusinessName,
            industry: setup?.industry ?? setup?.Industry ?? '',
            description: setup?.description ?? setup?.Description ?? '',
            address: value.address,
            city: value.city,
            state: this.selectedBusinessState?.abbreviation ?? this.selectedBusinessState?.name,
            zip: value.zipCode,
            country: setup?.country ?? setup?.Country ?? 'US',
            phone: setup?.phone ?? setup?.Phone ?? '',
            logo: setup?.logo ?? setup?.Logo ?? '',
            businessLogo: setup?.businessLogo ?? setup?.BusinessLogo ?? setup?.logo ?? '',
            preferredShippingProvider: setup?.preferredShippingProvider ?? setup?.PreferredShippingProvider ?? '',
            businessId: setup?.businessId ?? setup?.BusinessId,
        };

        return this._businessSetupService.saveBusinessInformationV2(payload).pipe(
            map((res: any) => {
                if (!res?.success) return false;
                Object.assign(setup, {
                    address: payload.address, Address: payload.address,
                    city: payload.city, City: payload.city,
                    state: payload.state, State: payload.state,
                    zip: payload.zip, Zip: payload.zip,
                });
                return true;
            }),
            catchError(() => of(false)),
        );
    }

    onShowShippingAddress(event: any): void {
        this.isBusinessAddressShipping = !!event?.checked;
    }

    /** True whenever there's nothing left to collect before submitting — business address (no form
     *  needed) or a fully-filled-out alternate address. Hardware-agnostic on purpose: it's only ever
     *  consulted where hardware is actually selected (canSubmit, the payload's billTo). */
    get shippingAddressValid(): boolean {
        return this.isBusinessAddressShipping || this.shippingForm.valid;
    }

    goBack(): void {
        // The "Add a card" precheck gate (see awaitingCardForPrecheck's doc comment) fires BEFORE
        // any order is submitted — there's nothing placed yet to leave behind, so Back should just
        // close this step and return to the still-intact checkout cart, not navigate away to plan
        // picking as if this were a normal "leave checkout" action.
        if (this.showAddCardStep && this.awaitingCardForPrecheck) {
            this.showAddCardStep = false;
            this.awaitingCardForPrecheck = false;
            return;
        }
        // The in-page "Back" pill is a deliberate, expected step back to plan picking, not an
        // accidental exit — same reasoning changePlan() below already applies, so it shouldn't be
        // interrupted by the leave-confirm dialog canDeactivate() otherwise shows for any
        // navigation away from this page. Selections are already persisted (persistCartState()),
        // so nothing is lost by skipping the warning here.
        this.skipNextLeaveConfirm = true;
        // Usage-only checkout (Buy more/Add) never went through plan selection, so there's no
        // "back to picking a plan" step to return to — go back to Subscription Management instead.
        if (this.isUsageOnlyCheckout) {
            this._router.navigate(['/app/billing-and-plans']);
            return;
        }
        this._router.navigate(['/change-plan']);
    }

    /** Post-purchase success screen's CTA — always returns to Subscription Management,
     *  regardless of checkout type, unlike goBack() which routes back into plan picking. */
    goToSubscriptionManagement(): void {
        this._router.navigate(['/app/billing-and-plans']);
    }

    /** "Change plan" pill — same destination as goBack() (/change-plan), but deliberately skips
     *  the leave-confirm warning: picking a different plan is a normal, expected step in this flow,
     *  not an accidental exit, so warning here would just be friction on every use. */
    changePlan(): void {
        this.skipNextLeaveConfirm = true;
        this.goBack();
    }

    goToPricing(): void {
        window.open('https://lincsell.com/pricing/', '_blank');
    }

    // Consumed once by the very next canDeactivate() check — set by changePlan() so that specific
    // navigation bypasses the leave-confirm dialog while every other route away from this page
    // (Back pill, the topbar X, browser Back/Forward) still goes through it as normal.
    private skipNextLeaveConfirm = false;

    /** Wired to UnsavedChangesGuard on this route — fires for the in-page Back button, the browser
     *  Back/Forward buttons, and any other router-driven navigation away from this page. Selections
     *  are already persisted to localStorage (persistCartState()) so nothing is technically lost on
     *  a reload, but navigating away without confirming still abandons the order itself, so this
     *  warns before that happens whenever there's something in the cart beyond the plan's defaults. */
    canDeactivate(): boolean | Promise<boolean> {
        if (this.skipNextLeaveConfirm) {
            this.skipNextLeaveConfirm = false;
            return true;
        }
        // showAddCardStep only renders after confirm() has already run — the order itself is
        // already placed at that point (only the optional card-on-file prompt is still pending) —
        // so leaving from there isn't abandoning an unconfirmed order the way leaving beforehand is.
        if (this.submitSucceeded || this.showAddCardStep || !this.hasAnyUsageSelection) return true;
        this.showLeaveConfirm = true;
        return new Promise<boolean>((resolve) => {
            this.pendingLeaveResolve = resolve;
        });
    }

    /** Topbar X button — same confirm-before-leaving guarantee canDeactivate() gives router-driven
     *  navigation (Back button, browser Back/Forward), for a direct click that never goes through
     *  the router guard at all. Present on every screen this page renders, including the
     *  showAddCardStep/submitSucceeded "slip" screens, where — same as goBack()/canDeactivate() —
     *  there's nothing left to confirm since the order is already placed (or there was never
     *  anything in the cart to lose). */
    requestExit(): void {
        if (this.submitSucceeded || this.showAddCardStep || !this.hasAnyUsageSelection) {
            this.goBack();
            return;
        }
        this.exitRequestedManually = true;
        this.showLeaveConfirm = true;
    }

    confirmLeave(leave: boolean): void {
        this.showLeaveConfirm = false;
        if (this.exitRequestedManually) {
            this.exitRequestedManually = false;
            // The X button is "exit checkout" (abandon the whole order), not "back one step" —
            // unlike goBack()/the Back pill (which return to plan picking so the cart can be
            // resumed), confirming here destroys the cart entirely and lands on Subscription
            // Management, matching the request/discard semantics of what this confirm dialog just
            // warned about ("nothing will be charged and this order won't be placed").
            if (leave) {
                this.clearPersistedCartState();
                localStorage.removeItem('selectedPlanDetails');
                this._signup.selectedPlanDetails = null;
                this.skipNextLeaveConfirm = true;
                this._router.navigate(['/app/billing-and-plans']);
            }
            return;
        }
        this.pendingLeaveResolve?.(leave);
        this.pendingLeaveResolve = null;
    }

    /** Merges the live plan the user picked on /change-plan (real cosmosId/pricing) with the local
     *  catalog entry (addons/planPackages/register-location defaults/hardware flag) by cosmosId —
     *  same matching approach as change-plan.component.ts's displayPlanCards. Addons are deep-cloned
     *  so toggling selection here never mutates PlanDataService's shared catalog array. */
    /** One-shot hint set by billing-and-plans.component.ts's "Buy more"/"Add" actions on an
     *  already-subscribed account — this page is normally reached only via full plan selection
     *  (see class doc comment), so those actions land here wanting a specific register/location
     *  bump or add-on pre-checked rather than a blank cart. Read once and removed immediately so a
     *  later, unrelated visit to /check-out (e.g. from /change-plan) never re-applies it. */
    private consumeUsageIntent(): {
        skipHardware?: boolean;
        bumpRegister?: boolean;
        bumpLocation?: boolean;
        presetAddonId?: any;
        // Set by hardware-shop.component.ts's "Proceed to checkout" — opens hardwareSupported for
        // this usage-only trip (normally forced off, see resolvePlan()) and hides the
        // registers/locations/add-ons sections that don't apply to a hardware-only purchase.
        allowHardware?: boolean;
        hardwareOnly?: boolean;
        presetHardware?: { id: any; quantity: number }[];
    } | null {
        const raw = sessionStorage.getItem('checkout-usage-intent');
        if (!raw) return null;
        sessionStorage.removeItem('checkout-usage-intent');
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }

    private resolvePlan(): void {
        let raw = this._signup.selectedPlanDetails;
        if (!raw) {
            try {
                raw = JSON.parse(localStorage.getItem('selectedPlanDetails'));
            } catch {
                raw = null;
            }
        }
        if (!raw) {
            this._router.navigate(['/change-plan']);
            return;
        }

        const usageIntent = this.consumeUsageIntent();
        this.isUsageOnlyCheckout = !!usageIntent;
        this.hardwareOnlyCheckout = !!usageIntent?.hardwareOnly;

        const cosmosId = raw.cosmosId ?? raw.CosmosId ?? null;
        const catalogMatch =
            this._planData.list.find((p: any) => cosmosId != null && String(p.cosmosId) === String(cosmosId)) ||
            this._planData.list.find((p: any) => String(p.planName).toLowerCase() === String(raw.title ?? raw.planName ?? '').toLowerCase());

        this.plan = {
            ...(catalogMatch || {}),
            ...raw,
            planName: raw.title ?? raw.planName ?? catalogMatch?.planName,
            cosmosId: cosmosId ?? catalogMatch?.cosmosId,
            planId: raw.planId ?? catalogMatch?.id,
            // Plan price comes from the live API plan only (no local plan-list.json price). The
            // per-period `prices` array is read first — it's keyed by periodString, so it can't be
            // confused with another package's price the way a bare monthlyPrice field was (a "Plus"
            // checkout once sent subTotalCost 79, the Additional Locations package's price). Refreshed
            // again from GetSubscriptions in loadUsageLimits().
            monthlyPrice: this.livePlanPrice(raw, 'Monthly') ?? 0,
            yearlyPrice: this.livePlanPrice(raw, 'Yearly') ?? this.livePlanPrice(raw, 'Monthly') ?? 0,
            defaultLocation: catalogMatch?.defaultLocation ?? raw.defaultLocation ?? 1,
            defaultRegister: catalogMatch?.defaultRegister ?? raw.defaultRegister ?? 1,
            hardwareDevicesEnable: catalogMatch?.hardwareDevicesEnable ?? raw.hardwareDevicesEnable ?? false,
            additionalLocationsPurchase: catalogMatch?.additionalLocationsPurchase ?? raw.additionalLocationsPurchase ?? false,
            additionalRegistersPurchase: catalogMatch?.additionalRegistersPurchase ?? raw.additionalRegistersPurchase ?? false,
            addons: ((catalogMatch?.addons ?? raw.addons) || []).map((a: any) => ({
                ...a,
                selected: usageIntent?.presetAddonId != null && String(a.id) === String(usageIntent.presetAddonId),
                discountApplied: false,
            })),
            StripeProductKey: raw.StripeProductKey ?? catalogMatch?.StripeProductKey,
            StripeSandboxProductKey: raw.StripeSandboxProductKey ?? catalogMatch?.StripeSandboxProductKey,
            discountApplied: false,
        };

        this.registersAndLocationsList = [...LocationRegisterList].sort((a, b) => b.id - a.id).map((item) => {
            const defaultQty = item.id === 1 ? this.plan.defaultRegister : this.plan.defaultLocation;
            const ownedQty = this.resolveOwnedQuantity(item.id === 1 ? 1 : 2, defaultQty);
            const bump = (item.id === 1 && usageIntent?.bumpRegister) || (item.id === 2 && usageIntent?.bumpLocation);
            const quantity = ownedQty + (bump ? 1 : 0);
            return {
                ...item,
                quantity,
                ownedQuantity: ownedQty,
                defaultRegister: this.plan.defaultRegister,
                defaultLocation: this.plan.defaultLocation,
                selected: quantity > ownedQty,
            };
        });

        // A reload has no fresh usageIntent (it's one-shot, consumed on first read above) — restore
        // whatever the user had actually chosen before the reload, scoped to this same plan so a
        // stale cart from an earlier, unrelated checkout session never leaks in.
        const persistedCart = usageIntent ? null : this.readPersistedCartState();
        if (persistedCart && String(persistedCart.planCosmosId) === String(this.plan.cosmosId)) {
            this.isUsageOnlyCheckout = persistedCart.isUsageOnlyCheckout;
            this.hardwareOnlyCheckout = !!persistedCart.hardwareOnlyCheckout;
            this.billingInterval = persistedCart.billingInterval;
            this.registersAndLocationsList.forEach((item) => {
                const addedQty = item.id === 1 ? persistedCart.registerAddedQuantity : persistedCart.locationAddedQuantity;
                const qty = item.id === 1 ? persistedCart.registerQuantity : persistedCart.locationQuantity;
                const ownedQty = item.ownedQuantity ?? 0;
                if (addedQty == null && qty == null) return;
                // Never restore below what the account actually owns now — the persisted value
                // could predate a purchase made elsewhere (e.g. Subscription Management) since this
                // cart was last saved.
                item.quantity = addedQty != null ? ownedQty + Math.max(0, this.toNumber(addedQty)) : ownedQty;
                item.selected = item.quantity > (item.ownedQuantity ?? 0);
            });
            // Kept around so mergeLiveAddons() can re-apply this after it swaps in the live addon
            // list — see that method's doc comment for why a one-shot pass here isn't enough on its
            // own (an addon that only exists in the live catalog, not the local one, has no row to
            // restore onto yet at this point in resolvePlan()).
            this.pendingAddonSelection = persistedCart;
            this.applyPersistedAddonSelections();
            if (persistedCart.businessAddress) {
                this.businessAddressForm.patchValue(persistedCart.businessAddress);
                if (persistedCart.businessAddressState) {
                    const matchedState = this.usaStates.find(
                        (s: any) => s.abbreviation === persistedCart.businessAddressState || s.name === persistedCart.businessAddressState
                    );
                    if (matchedState) this.selectedBusinessState = matchedState;
                }
            }
        } else {
            this.clearPersistedCartState();
        }

        // A hardware-only trip never shows the Locations & registers section at all — so restoring
        // a persisted register/locationQuantity from an earlier, unrelated "Buy more" session (the
        // cart-state key isn't scoped by checkout type) would silently add register/location
        // charges to the Order Summary that the merchant has no UI here to see or remove. Force back
        // to exactly what's owned whenever this is a hardware-only checkout, regardless of source.
        if (this.hardwareOnlyCheckout) {
            this.registersAndLocationsList.forEach((item) => {
                item.quantity = item.ownedQuantity ?? item.quantity;
                item.selected = false;
            });
            this.plan.addons.forEach((addon: any) => { addon.selected = false; });
        }

        // "Buy more"/"Add" from Subscription Management is only ever adding registers, locations
        // or an add-on to the current plan — never a hardware purchase, so that block stays hidden
        // even when the plan itself supports hardware. hardwareOnlyCheckout (set only by
        // hardware-shop.component.ts's trip here) is the one usage-only case that opens it back up;
        // it's derived from isUsageOnlyCheckout/hardwareOnlyCheckout (not the one-shot intent
        // directly) so a reload — which restores both above but has no intent to re-read — still
        // resolves the same way.
        this.hardwareSupported = this.HARDWARE_PURCHASE_ENABLED
            && !!this.plan.hardwareDevicesEnable
            && (!this.isUsageOnlyCheckout || this.hardwareOnlyCheckout);
        if (this.hardwareSupported) {
            this.getHardwareList(
                persistedCart?.selectedHardware ?? usageIntent?.presetHardware,
                !persistedCart && !!usageIntent?.presetHardware,
            );
        }

        this.loadUsageLimits();

        // Write the resolved state (isUsageOnlyCheckout in particular) out immediately, rather than
        // waiting for the first increment()/toggleAddon()/etc. — persistCartState() otherwise only
        // fires on a user action, so a reload landing before any interaction (e.g. right after
        // "Buy more" navigates here) would find no matching localStorage record, fall through to
        // the defaults above, and wrongly show "Change plan" and the hardware section again.
        this.persistCartState();

        this.tryClearLoading();
    }

    // --- Cart-state persistence: survives a reload of this page without losing what the merchant
    // already picked (register/location quantities, add-on selections, hardware selections). Coupon
    // codes are deliberately excluded — re-validating one automatically on load has its own edge
    // cases (expired, single-use) that are safer left to an explicit re-entry. ---
    private readonly cartStateStorageKey = 'checkout-cart-state';

    // Set by resolvePlan() whenever a persisted cart matched the current plan — kept around (rather
    // than only applied once, inline) so mergeLiveAddons() can re-run the same restore after it
    // replaces this.plan.addons with the live catalog's addons. See applyPersistedAddonSelections()'s
    // doc comment for why a single restore pass in resolvePlan() alone isn't enough.
    private pendingAddonSelection: any = null;

    /** Restores addon.selected from pendingAddonSelection (this reload's matched persisted cart, if
     *  any) onto whatever addons currently live on this.plan.addons — matched by planName, which is
     *  stable across the local plan-list.json catalog and the live GetSubscriptions catalog, unlike
     *  id (mergeLiveAddons() can swap it, see resolvePlan()'s addon-restore comment). Called twice:
     *  once synchronously in resolvePlan() against the local-catalog addon list, and again inside
     *  mergeLiveAddons() once it swaps in the live addon list — an addon that only exists in the
     *  live catalog (not the local one for this plan) has no row to restore onto during the first
     *  call, so skipping the second call would silently leave it unchecked after every reload. */
    private applyPersistedAddonSelections(): void {
        const cart = this.pendingAddonSelection;
        if (!cart || !this.plan) return;
        this.plan.addons.forEach((addon: any) => {
            const name = String(addon.planName || '').trim().toLowerCase();
            addon.selected =
                !!cart.selectedAddonIds?.some((id: any) => String(id) === String(addon.id)) ||
                !!cart.selectedAddonNames?.some((n: any) => String(n || '').trim().toLowerCase() === name);
        });
    }

    // localStorage is scoped to the browser, not the logged-in account — without this check, a
    // cart abandoned mid-checkout on one account (never hit any of the clearPersistedCartState()
    // call sites) silently resurfaces on a different account signed into the same browser whenever
    // it happens to pick the same plan (resolvePlan() only matched on planCosmosId). Every persisted
    // cart is stamped with the userID that created it; a mismatch (or an old cart with no stamp at
    // all) is treated as not present.
    private currentUserId(): string | null {
        const id = GlobalService.userInfo?.userID ?? GlobalService.userInfo?.userId ?? GlobalService.userInfo?.UserID;
        return id != null ? String(id) : null;
    }

    private readPersistedCartState(): any {
        try {
            const raw = localStorage.getItem(this.cartStateStorageKey);
            if (!raw) return null;
            const cart = JSON.parse(raw);
            const ownerId = cart?.userId != null ? String(cart.userId) : null;
            const currentId = this.currentUserId();
            if (ownerId == null || currentId == null || ownerId !== currentId) return null;
            return cart;
        } catch {
            return null;
        }
    }

    private clearPersistedCartState(): void {
        localStorage.removeItem(this.cartStateStorageKey);
        // Also drop the selected-plan pointer itself — otherwise it survives a successful
        // checkout (or a manual exit) and silently pre-selects the same plan on the next
        // visit, including for a brand-new account created after logout.
        localStorage.removeItem('selectedPlanDetails');
        sessionStorage.removeItem('checkout-usage-intent');
        this._signup.selectedPlanDetails = null;
    }

    persistCartState(): void {
        if (!this.plan) return;
        const registerItem = this.registersAndLocationsList.find((i) => i.id === 1);
        const locationItem = this.registersAndLocationsList.find((i) => i.id === 2);
        localStorage.setItem(this.cartStateStorageKey, JSON.stringify({
            userId: this.currentUserId(),
            planCosmosId: this.plan.cosmosId,
            isUsageOnlyCheckout: this.isUsageOnlyCheckout,
            hardwareOnlyCheckout: this.hardwareOnlyCheckout,
            billingInterval: this.billingInterval,
            registerQuantity: registerItem?.quantity,
            locationQuantity: locationItem?.quantity,
            registerAddedQuantity: Math.max(0, this.toNumber(registerItem?.quantity) - this.toNumber(registerItem?.ownedQuantity)),
            locationAddedQuantity: Math.max(0, this.toNumber(locationItem?.quantity) - this.toNumber(locationItem?.ownedQuantity)),
            selectedAddonIds: this.optionalAddons.filter((a: any) => a.selected).map((a: any) => a.id),
            selectedAddonNames: this.optionalAddons.filter((a: any) => a.selected).map((a: any) => a.planName),
            selectedHardware: this.hardwaresList.filter((h) => h.selected).map((h) => ({ id: h.id, quantity: h.selectedQuantity })),
            businessAddress: this.businessAddressForm?.value,
            businessAddressState: this.selectedBusinessState?.abbreviation ?? this.selectedBusinessState?.name,
        }));
    }

    /** Caps how many extra registers/locations can be bought — same "Locations"/"Registers"
     *  typeString limit bizz-customer-plan-configuration.component.ts already parses off the live
     *  plan catalog (GetSubscriptions/V1, the richer endpoint — the local plan-list.json catalog
     *  this page otherwise uses has no numeric limit field at all). A missing/unmatched/unlimited
     *  entry, or the request failing outright, leaves the cap at null (no limit enforced) rather
     *  than blocking checkout over it. */
    private loadUsageLimits(): void {
        const planName = String(this.plan?.planName || '').trim().toLowerCase();
        if (!planName) {
            this.isLoadingUsageLimits = false;
            return;
        }
        this._subscriptionPlanService.getSubscriptionPlanList().pipe(
            timeout(10000),
            catchError(() => of(null)),
            finalize(() => this.isLoadingUsageLimits = false),
        ).subscribe((res: any) => {
            const plans = res?.data ?? res?.Data ?? res ?? [];
            const match = (Array.isArray(plans) ? plans : []).find(
                (p: any) => String(p?.title ?? p?.Title ?? '').trim().toLowerCase() === planName
            );
            if (!match) return;
            this.mergeLivePlanPrice(match);
            this.maxRegisters = this.parseLimitByTypeString(match, ['Registers']);
            this.maxLocations = this.parseLimitByTypeString(match, ['Locations']);
            const featureData: any[] = Array.isArray(match?.featureData) ? match.featureData : [];
            const registersEntry = featureData.find((f: any) => f?.typeString === 'Registers');
            const locationsEntry = featureData.find((f: any) => f?.typeString === 'Locations');
            this.canBuyExtraRegisters = registersEntry ? (registersEntry.unlimited === true || registersEntry.addMore === true) : null;
            this.canBuyExtraLocations = locationsEntry ? (locationsEntry.unlimited === true || locationsEntry.addMore === true) : null;
            this.mergeLiveRegisterLocationPrices(registersEntry, locationsEntry);
            this.mergeLiveAddons(match);
        });
    }

    /** Reads a plan's price for one period off an API plan object: the `prices` array
     *  ({ periodString, value }) first, then the flat monthlyPrice/yearlyPrice fields. Returns null
     *  when the plan carries no finite positive price for that period. */
    private livePlanPrice(plan: any, period: 'Monthly' | 'Yearly'): number | null {
        const prices: any[] = Array.isArray(plan?.prices) ? plan.prices : [];
        const entry = prices.find((p: any) => String(p?.periodString).toLowerCase() === period.toLowerCase());
        const value = Number(entry?.value ?? (period === 'Monthly' ? plan?.monthlyPrice : plan?.yearlyPrice));
        return Number.isFinite(value) && value > 0 ? value : null;
    }

    /** Applies the GetSubscriptions plan's own price over the one resolvePlan() read off the plan
     *  the merchant picked. Skipped per-period when the live entry has no usable price. */
    private mergeLivePlanPrice(match: any): void {
        const monthly = this.livePlanPrice(match, 'Monthly');
        const yearly = this.livePlanPrice(match, 'Yearly');
        if (monthly != null) this.plan.monthlyPrice = monthly;
        if (yearly != null) this.plan.yearlyPrice = yearly;
    }

    /** Overrides the static plan-list.json/location-resgister.ts unit prices for Registers/Locations
     *  (registersAndLocationsList's item.price, which every cost calc — extraCostFor(),
     *  fullExtraCostFor(), registerLocationExtraTotal, the confirm() payload — reads from) with the
     *  live GetSubscriptions featureData `price` field once it resolves. Mirrors mergeLiveAddons()'s
     *  reasoning for addons: the local catalog is a fallback for the moment before this call
     *  resolves, not a source of truth, so a stale local price should never win once the live value
     *  is known. Skipped per-item when the live entry is missing or its price isn't a finite number,
     *  leaving the local fallback in place rather than zeroing out the price. */
    private mergeLiveRegisterLocationPrices(registersEntry: any, locationsEntry: any): void {
        const applyPrice = (item: LocationRegisterType | undefined, entry: any) => {
            if (!item || !entry) return;
            const price = Number(entry.price);
            if (Number.isFinite(price)) item.price = price;
        };
        applyPrice(this.registersAndLocationsList.find((i) => i.id === 1), registersEntry);
        applyPrice(this.registersAndLocationsList.find((i) => i.id === 2), locationsEntry);
    }

    /** Add-ons shown here start out sourced from the local plan-list.json catalog (resolvePlan()) —
     *  once the live GetSubscriptions plan loads, this replaces that list with subscriptionAddons —
     *  the API's own curated, plan-specific addon array (real monthly/yearly split, real Stripe
     *  product keys) — so pricing/existence isn't hardcoded in a file that can drift from what the
     *  API actually charges. Deliberately NOT sourced from featureData's isAddon:true rows: those
     *  are informational feature/pricing text for the plan-comparison page, not a curated list of
     *  what's actually purchasable for *this* plan — mixing them in showed add-ons (Static Website,
     *  Basic eCommerce Store, Customer Loyalty Program) that don't actually belong to this plan.
     *  `selected` is carried over from whatever this.plan.addons already had, so a usage-intent
     *  preset or a restored persisted-cart selection isn't lost when this replaces it. */
    /** Fallback Stripe product keys for addons that aren't in any local catalog file at all
     *  (addon-list.json only has Standard Customer App / Advanced Loyalty Program) — if the live
     *  GetSubscriptions API's subscriptionAddons entry for one of these doesn't carry its own
     *  stripeProductKey, confirm() would otherwise send an empty key, and the backend's Stripe sync
     *  has nothing to match against, silently dropping the item (present in subscriptionPlanPkg /
     *  counted in totalCost, but never actually added to the Stripe subscription). */
    private readonly ADDON_STRIPE_KEY_FALLBACKS: Record<string, { prod: string; sandbox: string }> = {
        'custom online store': { prod: 'prod_SEjfCBQt7FaNrZ', sandbox: 'prod_SGIix4rbiqYnTc' },
    };

    private mergeLiveAddons(match: any): void {
        const subscriptionAddons = Array.isArray(match?.subscriptionAddons) ? match.subscriptionAddons : [];
        if (!subscriptionAddons.length) return;

        const existingByName = new Map<string, any>(
            (this.plan.addons || []).map((a: any) => [String(a.planName || '').trim().toLowerCase(), a])
        );

        this.plan.addons = subscriptionAddons.filter((a: any) => a?.title).map((a: any) => {
            const name = String(a.title).trim();
            const localMatch = existingByName.get(name.toLowerCase());
            const fallbackKeys = this.ADDON_STRIPE_KEY_FALLBACKS[name.toLowerCase()];
            return {
                id: a.id ?? localMatch?.id,
                planName: name,
                detail: a.detail || localMatch?.detail || name,
                selected: !!localMatch?.selected,
                isMonthly: a.isMonthly ?? true,
                allowPaidTrial: a.allowPaidTrial ?? false,
                trialPrice: this.toNumber(a.trialPrice),
                trialMonth: a.trialMonth ?? 0,
                monthlyPrice: this.toNumber(a.monthlyPrice),
                yearlyPrice: this.toNumber(a.yearlyPrice ?? a.monthlyPrice),
                isAddon: true,
                defaultAddedInSubscription: a.defaultAddedInSubscription ?? false,
                withTaxPrice: 0,
                StripeProductKey: a.stripeProductKey ?? localMatch?.StripeProductKey ?? fallbackKeys?.prod,
                StripeSandboxProductKey: a.stripeSandboxProductKey ?? localMatch?.StripeSandboxProductKey ?? fallbackKeys?.sandbox,
                discountApplied: false,
                // Custom Online Store's cost varies with the amount of customization involved, so
                // its listed price is a starting point rather than a flat rate — shown with a "+"
                // suffix (see addon-price in the template).
                priceIsStartingAt: name.toLowerCase() === 'custom online store',
            };
        });

        // `selected: !!localMatch?.selected` above only covers an addon that already had a row in
        // the local-catalog addon list (resolvePlan()'s first restore pass ran against that list).
        // An addon that's live-catalog-only for this plan (no local-catalog entry at all — a real,
        // confirmed case: plan-list.json's "Advanced" plan only lists "Standard Customer App"
        // locally, but the live API also curates "Advanced Loyalty Program"/"Custom Online Store"
        // for it) has no localMatch and so always starts unselected here, silently dropping a
        // reload-restored selection for exactly those addons. Re-run the same restore now that the
        // live-only rows actually exist.
        this.applyPersistedAddonSelections();
    }

    /** The account's real current count for a resource — plan default plus all past purchases.
     *  Prefers the account-level total field (PerNoOfRegister/NoOfLocations) plus the Type 6
     *  (AdditionalRegister) / Type 7 (AdditionalLocationPlus1Reg) SubscribedFeatures Qty, same
     *  source billing-and-plans.component.ts's registerCount/extraRegisterCount (etc.) now read —
     *  see that getter's doc comment. Falls back to the older noOfRegisters/noOfLocations +
     *  title/isAddon heuristic when no SubscribedFeatures entry carries a type/Type field, which
     *  covers stale Cosmos documents from before the account was re-saved. This becomes the
     *  stepper's starting quantity and the reference increment()/decrement()/extraCostFor()/
     *  confirm() use for "how many NEW units" — using the plan's static default instead, once a
     *  merchant already owns more than that default, would understate what they own on screen AND
     *  send a too-low NoOfRegisters/NoOfLocations to the backend on the next purchase (item.quantity
     *  is sent as the account's new absolute total), silently reverting previously bought capacity. */
    private resolveOwnedQuantity(resourceId: 1 | 2, defaultQty: number): number {
        const setup = this.currentBusinessSetup;
        return readOwnedRecurringQuantity(setup, this.currentInvoice, resourceId, defaultQty);
    }

    private parseLimitByTypeString(planData: any, typeStrings: string[]): number | null {
        const candidateKeys = ['featureData', 'FeatureData', 'planPackages', 'PlanPackages', 'packages', 'Packages', 'limits', 'Limits', 'planLimits', 'PlanLimits'];
        let packages: any[] = [];
        for (const key of candidateKeys) {
            if (Array.isArray(planData?.[key]) && planData[key].some((p: any) => p?.typeString)) {
                packages = planData[key];
                break;
            }
        }
        const pkg = packages.find((p: any) =>
            typeStrings.some((t) => String(p.typeString || '').toLowerCase() === t.toLowerCase())
        );
        if (!pkg || pkg.unlimited === true) return null;
        const num = Number(pkg.value);
        return Number.isFinite(num) ? num : null;
    }

    // TODO re-enable: the live GetSubscriptions API currently returns Registers/Locations
    // featureData values equal to the plan's own included amount (e.g. Premium's cap = 1, same as
    // what's already included), which wrongly blocks "Buy more" entirely instead of capping it at
    // some higher number. Flip this back on once that plan-config data is corrected.
    private readonly ENFORCE_LIVE_USAGE_LIMITS = false;

    // Re-enabled: hardware purchasing (fresh-plan checkout + the standalone hardware-shop.component.ts
    // "Buy hardware" trip via hardwareOnlyCheckout). Flip back to false to hide it everywhere again.
    private readonly HARDWARE_PURCHASE_ENABLED = true;

    // TODO remove: FinishSignUpProcess is currently erroring for some usage-only (Buy more)
    // purchases — until that's fixed on the backend, don't let the failure block the merchant from
    // reaching the add-card/success step. Flip back to false once FinishSignUpProcess is reliable
    // again, so a genuine failure there correctly stops the flow instead of being swallowed.
    private readonly TOLERATE_FINISH_SIGNUP_FAILURE_FOR_USAGE_ONLY = true;

    maxQuantityFor(item: LocationRegisterType): number | null {
        if (!this.ENFORCE_LIVE_USAGE_LIMITS) return null;
        return item.id === 1 ? this.maxRegisters : this.maxLocations;
    }

    /** Whether the SELECTED plan (this.plan) allows buying extra of this resource at all —
     *  independent of ENFORCE_LIVE_USAGE_LIMITS's numeric cap, which only governs *how many* extra
     *  can be bought once buying is allowed in the first place. Checks both sources and allows it
     *  if EITHER says yes: the live GetSubscriptions featureData (addMore/unlimited) can lag behind
     *  actual plan entitlements, so a plan explicitly marked purchasable in the local catalog
     *  (additionalRegistersPurchase/additionalLocationsPurchase) shouldn't be blocked by a stale
     *  live flag during a plan switch. */
    private canBuyExtraFor(item: LocationRegisterType): boolean {
        const liveFlag = item.id === 1 ? this.canBuyExtraRegisters : this.canBuyExtraLocations;
        const plan = this.plan;
        const localFlag = item.id === 1 ? !!plan?.additionalRegistersPurchase : !!plan?.additionalLocationsPurchase;
        return liveFlag === true || localFlag;
    }

    isAtMaxQuantity(item: LocationRegisterType): boolean {
        if (!this.canBuyExtraFor(item)) {
            return item.quantity >= (item.ownedQuantity ?? item.quantity);
        }
        const max = this.maxQuantityFor(item);
        return max != null && item.quantity >= max;
    }

    private toNumber(value: any): number {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    }

    private applyUsageOnlySubscriptionTotals(data: any): void {
        if (!this.isUsageOnlyCheckout) return;
        const totals = buildRecurringSubscriptionTotals(this.recurringTotalDisplay, this.taxRate);
        data.subscription.subTotalCost = totals.subTotalCost;
        data.subscription.TotalTaxAmount = totals.TotalTaxAmount;
        data.subscription.totalCost = totals.totalCost;
    }

    get optionalAddons(): any[] {
        return (this.plan?.addons || [])
            .filter((a: any) => !a.defaultAddedInSubscription)
            .filter((a: any) => !this.isAddonAlreadyPurchased(a));
    }

    /** Excludes an add-on already paid for from being offered here again — without this, a merchant
     *  could re-select and re-purchase (double-charge) something they already own. Checked two ways,
     *  since either alone can miss a real purchase:
     *  1. subscription.subscribedFeatures, matched by title with isActive: true — the backend's own
     *     purchase-confirmation record (see billing-and-plans.component.ts's subscriptionAddonsList
     *     doc comment for why the flat businessSetup[0] boolean flags aren't trustworthy here).
     *  2. subscription.subscriptionPlanPkg, matched by packageId === addon.id — the actual billed
     *     transaction line items. Confirmed against a real account: a $199 Custom Online Store
     *     purchase showed up here (genuinely charged) but never got an active SubscribedFeatures
     *     entry — the backend's downstream activation step can itself fail, so relying only on
     *     SubscribedFeatures let an already-paid-for add-on keep showing as purchasable. */
    private isAddonAlreadyPurchased(addon: any): boolean {
        const setup = this.currentBusinessSetup;
        const subscription = setup?.subscription ?? setup?.Subscription ?? {};
        const name = String(addon?.planName || '').trim().toLowerCase();

        const features: any[] = subscription?.subscribedFeatures ?? subscription?.SubscribedFeatures ?? [];
        const activeInFeatures = features.some((f: any) => {
            const title = String(f?.title ?? f?.Title ?? '').trim().toLowerCase();
            const isActive = f?.isActive ?? f?.IsActive;
            return title === name && !!isActive;
        });
        if (activeInFeatures) return true;

        const addonId = addon?.id != null ? String(addon.id) : null;
        if (!addonId) return false;
        const purchasedLines: any[] = subscription?.subscriptionPlanPkg ?? subscription?.SubscriptionPlanPkg ?? [];
        return purchasedLines.some((line: any) => String(line?.packageId ?? line?.PackageId ?? '') === addonId);
    }

    get hasSelectedAddons(): boolean {
        return this.optionalAddons.some((a: any) => a.selected);
    }

    /** Mirror of optionalAddons — add-ons already active on the account, which optionalAddons
     *  deliberately excludes (see isAddonAlreadyPurchased's doc comment) so they can't be
     *  re-purchased. Used to show what's already billed in the Order Summary breakdown, so the
     *  Recurring total isn't a lump sum with no explanation of what's already on the account. */
    get alreadyPurchasedAddons(): any[] {
        return (this.plan?.addons || [])
            .filter((a: any) => !a.defaultAddedInSubscription)
            .filter((a: any) => this.isAddonAlreadyPurchased(a));
    }

    /** Active account features (subscribedFeatures) that the NEWLY SELECTED plan doesn't carry
     *  forward at all — neither as one of its own purchasable addons (so alreadyPurchasedAddons,
     *  which only ever looks at plan.addons, can't surface it) nor bundled in as an
     *  included:true planPackages entry. Confirmed real case: Custom Online Store is included
     *  free in Premium's planPackages but isn't in Standard/Plus's addons or planPackages at all —
     *  switching down would silently drop it with no warning anywhere on this page. Excludes
     *  register/location extras (Type 6/7), which have their own dedicated "already purchased"
     *  UI in the Locations & registers section. */
    get featuresLostOnSwitch(): { title: string }[] {
        const subscription = this.currentBusinessSetup?.subscription ?? this.currentBusinessSetup?.Subscription ?? {};
        const features: any[] = subscription?.subscribedFeatures ?? subscription?.SubscribedFeatures ?? [];
        const planAddonNames = new Set(
            (this.plan?.addons || []).map((a: any) => String(a?.planName || '').trim().toLowerCase())
        );
        const includedPackageNames = new Set(
            (this.plan?.planPackages || [])
                .filter((p: any) => p?.included === true || p?.included === 'true')
                .map((p: any) => String(p?.title || '').trim().toLowerCase())
        );
        const seen = new Set<string>();
        const lost: { title: string }[] = [];
        for (const feature of features) {
            const isActive = feature?.isActive ?? feature?.IsActive;
            if (!isActive) continue;
            const type = Number(feature?.type ?? feature?.Type);
            if (type === 6 || type === 7) continue; // register/location extras — handled elsewhere
            const title = String(feature?.title ?? feature?.Title ?? '').trim();
            const key = title.toLowerCase();
            if (!title || seen.has(key)) continue;
            if (planAddonNames.has(key) || includedPackageNames.has(key)) continue;
            seen.add(key);
            lost.push({ title });
        }
        return lost;
    }

    get featuresLostOnSwitchLabel(): string {
        return this.featuresLostOnSwitch.map((f) => f.title).join(', ');
    }

    /** Usage-only checkout (Buy more/Add) now always lands at the plan's included defaults —
     *  nothing pre-bumped — so this is false until the merchant actually increments a register/
     *  location, checks an add-on, or picks hardware. Gates the Order Summary's empty state (a bare
     *  "$0.00 Recurring total" with no line items above it reads as broken) and canSubmit, so
     *  Confirm can't be clicked on an empty cart. */
    get hasAnyUsageSelection(): boolean {
        return this.registerLocationExtraTotal > 0 || this.hasSelectedAddons || this.hardwareTotal > 0;
    }

    /** True when this order is buying hardware only, with no new registers/locations/add-ons.
     *  Used to hide the "Billed next cycle" note on the Add Card step — that note is meant to
     *  flag a NEW recurring charge being added, but recurringTotalDisplay always folds in the
     *  account's existing plan fee too, so a hardware-only purchase would otherwise show a
     *  confusing monthly charge that has nothing to do with what's actually being bought here. */
    get isHardwareOnlyPurchase(): boolean {
        return this.hardwareTotal > 0 && this.registerLocationExtraTotal <= 0 && !this.hasSelectedAddons;
    }

    setBillingInterval(interval: 'Monthly' | 'Yearly'): void {
        if (this.billingInterval === interval) return;
        this.billingInterval = interval;
        if (this.couponSuccess) {
            // Discount amounts were validated against the previous interval's price — safest to
            // require re-applying rather than silently carrying a possibly-stale discount forward.
            this.clearCoupon();
        }
        this.persistCartState();
    }

    get isFreeTrialAccount(): boolean {
        return !!this.currentBusinessSetup?.isFreeTrial;
    }

    get freeTrialEndDate(): string | Date | null {
        return this.currentBusinessSetup?.freeTrialEndDate ?? null;
    }

    get nextBillingDate(): string | Date | null {
        const subscription = this.currentBusinessSetup?.subscription ?? this.currentBusinessSetup?.Subscription;
        const nextChargeDate = subscription?.nextChargeDate ?? null;
        if (!nextChargeDate) return null;
        // Backend sends .NET's uninitialized DateTime default (0001-01-01) when a subscription
        // hasn't actually started billing yet — treat that the same as "not set".
        if (new Date(nextChargeDate).getFullYear() <= 1) return null;
        return nextChargeDate;
    }

    /** The account's plan before this checkout, resolved the same way billing-and-plans/change-plan
     *  resolve resolvedPlanName — businessSetup[0].subscription.planId (a cosmosId) matched against
     *  the local catalog. /check-out isn't only reached for a trial account's very first plan pick
     *  (despite the class doc comment) — change-plan.component.ts's goToCheckOut() routes here for
     *  any plan change during an active trial, including one where the account already had a
     *  different plan selected — so "what am I switching from" is real, useful context here. */
    get currentPlanName(): string | null {
        const subscription = this.currentBusinessSetup?.subscription ?? this.currentBusinessSetup?.Subscription;
        const cosmosId = subscription?.planId ?? subscription?.PlanId;
        if (!cosmosId) return null;
        const matched = this._planData.list.find((p: any) => String(p.cosmosId) === String(cosmosId));
        return matched?.planName ?? null;
    }

    /** Only worth showing when this checkout is a genuine change from a plan the account already
     *  had — not a usage-only "Buy more" cart (which never changes the plan itself), and not when
     *  there's nothing to switch from or the "current" plan happens to match what's selected. */
    get isSwitchingPlan(): boolean {
        return !this.isUsageOnlyCheckout && !!this.currentPlanName && this.currentPlanName !== this.plan?.planName;
    }

    // --- Registers & Locations ---
    increment(item: LocationRegisterType): void {
        if (this.isAtMaxQuantity(item)) return;
        item.quantity++;
        item.selected = item.quantity > (item.ownedQuantity ?? 0);
        this.persistCartState();
    }

    decrement(item: LocationRegisterType): void {
        // Can't decrement below what's already owned — this stepper only ever adds new units, it
        // never removes previously purchased ones.
        const floor = item.ownedQuantity ?? 0;
        if (item.quantity > floor) {
            item.quantity--;
        }
        item.selected = item.quantity > floor;
        this.persistCartState();
    }

    // --- Add-ons ---
    toggleAddon(addon: any, event: any): void {
        addon.selected = !!event?.checked;
        if (!addon.selected && addon.discountApplied) {
            this.clearCoupon();
        }
        this.persistCartState();
    }

    // --- Hardware ---
    private getHardwareList(persistedSelections?: { id: any; quantity: number }[], skipAlreadyPurchasedGuard = false): void {
        this.loadingHardwares = true;
        this._signup.getHardwarePlanListv2().pipe(
            timeout(10000),
            catchError(() => of({ success: false, data: [] })),
            finalize(() => this.loadingHardwares = false),
        ).subscribe((res: any) => {
            if (!res?.success || !Array.isArray(res.data)) {
                this.hardwaresList = [];
                this.hardwareCategories = [];
                return;
            }
            const groups = res.data;
            const safeItems = (i: number) => Array.isArray(groups[i]?.items) ? groups[i].items : [];
            const mainDevices = safeItems(0);
            const additionalDevices = safeItems(1);
            const peripherals = safeItems(2);

            // Reapplies a reload's persisted picks onto this freshly-fetched catalog, matched by id
            // — the catalog itself isn't persisted, only which items/quantities were selected.
            // Never restore a selection for hardware already actually purchased FROM A STALE
            // localStorage CART: that cart-state is only ever cleared on success paths that return
            // to this same page (chargeHardware()/completeUsageOnlyPurchase()) — a true
            // first-time-signup purchase redirects out to Stripe's hosted checkout and back to
            // /app/billing-and-plans on success, never back here, so that stale selection (id +
            // quantity) sits in localStorage indefinitely and would otherwise get silently
            // re-added to the cart the next time this same plan is picked again. This guard is
            // skipped for a fresh usageIntent.presetHardware (hardware-shop.component.ts's
            // "Proceed to checkout") — that's an explicit, just-made choice to buy MORE of an
            // item the merchant may already own (e.g. a second Stripe Reader M2), so blocking it
            // as "already purchased" would silently drop the very item they just selected.
            const restore = (id: any) => (!skipAlreadyPurchasedGuard && this.isHardwarePackageAlreadyPurchased(id))
                ? undefined
                : persistedSelections?.find((s) => String(s.id) === String(id));
            const toListItem = (v: any) => {
                const saved = restore(v.id);
                return {
                    ...v,
                    selectedQuantity: saved?.quantity || 0,
                    selectedTempQuantity: saved?.quantity || 0,
                    selectedInPopup: false,
                    selected: !!saved?.quantity,
                };
            };

            const peripheralTitles = ['Cash Drawer', 'Barcode Scanner', 'Receipt Printer'];
            const mainItems = mainDevices.map(toListItem);
            const additionalItems = additionalDevices.filter((f: any) => f?.isActive !== false).map(toListItem);
            const peripheralItems = peripherals.filter((f: any) => peripheralTitles.includes(f.title)).map(toListItem);

            this.hardwaresList = [...mainItems, ...additionalItems, ...peripheralItems];
            this.hardwareCategories = [
                { title: groups[0]?.packageTitle || groups[0]?.packageTypeString || 'Terminals', items: mainItems },
                { title: groups[1]?.packageTitle || groups[1]?.packageTypeString || 'Payment Devices', items: additionalItems },
                { title: groups[2]?.packageTitle || groups[2]?.packageTypeString || 'Accessories', items: peripheralItems },
            ].filter((category) => category.items.length);
            // The initial persistCartState() call in resolvePlan() runs synchronously right after
            // getHardwareList() is kicked off — it fires before this async catalog fetch resolves,
            // so it captures hardwaresList still empty and writes selectedHardware: [] to
            // localStorage. Re-persist now that the real selections (from presetHardware/a reload's
            // own persisted state) are actually applied, or a reload right after this one would find
            // nothing to restore.
            this.persistCartState();
        });
    }

    get selectedHardware(): any[] {
        return this.hardwaresList.filter((h) => h.selected);
    }

    /** True if this hardware catalog id already has a recorded purchase line on the account —
     *  same subscriptionPlanPkg source and packageId match hardware-shop.component.ts's
     *  purchasedHardwareList uses to render "Your hardware". Excludes the register/location
     *  packageIds ('1'/'2'), which are never hardware. */
    private isHardwarePackageAlreadyPurchased(packageId: any): boolean {
        const id = String(packageId ?? '');
        if (!id || id === '1' || id === '2') return false;
        const subscription = this.currentBusinessSetup?.subscription ?? this.currentBusinessSetup?.Subscription ?? {};
        const lines: any[] = subscription?.subscriptionPlanPkg ?? subscription?.SubscriptionPlanPkg ?? [];
        return lines.some((line: any) => String(line?.packageId ?? line?.PackageId ?? '') === id);
    }

    get isAnyHardwareInPopupSelected(): boolean {
        return this.hardwaresList.some((h) => h.selectedInPopup);
    }

    openHardwareDialog(): void {
        this.hardwaresList.forEach((h) => {
            h.selectedTempQuantity = h.selectedQuantity;
            h.selectedInPopup = h.selected;
            h.oldQty = h.selectedQuantity;
        });
        this.hardwareDialogVisible = true;
    }

    incrementHardwareQty(item: any): void {
        item.selectedTempQuantity = (item.selectedTempQuantity || 0) + 1;
        item.selectedInPopup = item.selectedTempQuantity > 0;
    }

    decrementHardwareQty(item: any): void {
        if (item.selectedTempQuantity > 0) {
            item.selectedTempQuantity--;
            item.selectedInPopup = item.selectedTempQuantity > 0;
        }
    }

    saveHardwareSelection(): void {
        this.hardwaresList.forEach((h) => {
            h.selectedQuantity = h.selectedTempQuantity;
            h.selected = h.selectedInPopup;
        });
        this.hardwareDialogVisible = false;
        this.persistCartState();
    }

    cancelHardwareSelection(): void {
        this.hardwaresList.forEach((h) => {
            h.selectedTempQuantity = h.oldQty ?? h.selectedQuantity;
            h.selectedInPopup = h.selected;
        });
        this.hardwareDialogVisible = false;
    }

    removeHardware(item: any): void {
        item.selected = false;
        item.selectedQuantity = 0;
        item.selectedTempQuantity = 0;
        this.persistCartState();
    }

    /** Inline +/- stepper on the checkout page's own selected-hardware row — distinct from
     *  incrementHardwareQty/decrementHardwareQty above, which only adjust selectedTempQuantity
     *  inside the "Add" picker dialog before Save is clicked. This adjusts the already-selected
     *  item's real selectedQuantity directly, so it persists immediately like the register/
     *  location steppers do. */
    incrementSelectedHardwareQty(item: any): void {
        item.selectedQuantity++;
        item.selectedTempQuantity = item.selectedQuantity;
        this.persistCartState();
    }

    decrementSelectedHardwareQty(item: any): void {
        if (item.selectedQuantity > 1) {
            item.selectedQuantity--;
            item.selectedTempQuantity = item.selectedQuantity;
            this.persistCartState();
        }
    }

    // --- Pricing ---
    get isTaxableTransaction(): boolean {
        const savedState = this.currentBusinessSetup?.state ?? this.currentBusinessSetup?.State;
        if (savedState) return savedState === 'TX';
        // No business address saved yet (the inline form is showing instead, see the
        // "Business address" section's *ngIf) — fall back to whatever state is currently picked
        // there so the Order Summary's tax line already matches what saveBusinessAddressForConfirm()
        // will save and submitOrder() will actually charge, instead of showing $0 tax right up
        // until the merchant clicks Proceed and only then finding out the real total was higher.
        // Applies regardless of whether hardware is in the cart — a subscription-only checkout
        // with no saved address needs the same live estimate, not a $0 placeholder.
        if (!this.hasBusinessAddress) {
            return this.selectedBusinessState?.abbreviation === 'TX';
        }
        return false;
    }

    get taxRate(): number {
        return this.isTaxableTransaction ? 8.25 : 0;
    }

    /** USA-standard "round half up" (round half away from zero) to the cent. Plain .toFixed(2) on
     *  a raw float division is a known JS trap for exact-tie amounts (e.g. (1.005).toFixed(2) gives
     *  "1.00", not "1.01") because the division's result isn't exactly representable in binary —
     *  Number.EPSILON nudges it back onto the correct side before rounding. Same pattern
     *  billing-and-plans-calculation.ts's roundCurrency() already uses. */
    private roundCurrency(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }

    private calculateTax(amount: number): number {
        if (!amount || !this.taxRate) return 0;
        return this.roundCurrency((amount * this.taxRate) / 100);
    }

    get planPeriodPrice(): number {
        // Usage-only checkout (Buy more/Add from Subscription Management) is adding to a plan the
        // account is already paying for — it isn't purchasing or changing the plan itself, so its
        // fee has no place in what's charged today.
        if (this.isUsageOnlyCheckout) return 0;
        return this.planDisplayPrice;
    }

    /** The plan's actual period price, regardless of checkout mode — used to show the plan line
     *  in the Order Summary (and fold into recurringTotalDisplay) even during a usage-only
     *  checkout, where planPeriodPrice itself is forced to 0 so today's charge stays add-ons-only. */
    get planDisplayPrice(): number {
        if (this.plan?.discountApplied && (this.lastCouponCategory === 'plan' || this.lastCouponCategory === 'subscription')) {
            return this.toNumber(this.plan.discountedPrice);
        }
        return this.billingInterval === 'Yearly' ? (this.plan?.yearlyPrice ?? 0) : (this.plan?.monthlyPrice ?? 0);
    }

    get addonsTotal(): number {
        return this.optionalAddons.filter((a: any) => a.selected).reduce((sum: number, a: any) => {
            const price = (this.lastCouponCategory === 'addon' && a.discountApplied)
                ? this.toNumber(a.discountedPrice)
                : (this.billingInterval === 'Yearly' ? this.toNumber(a.yearlyPrice) : this.toNumber(a.monthlyPrice));
            return sum + price;
        }, 0);
    }

    /** Ongoing cost of add-ons already active on the account — mirrors addonsTotal but for
     *  alreadyPurchasedAddons, so recurringTotalDisplay's "true combined bill" projection (see its
     *  doc comment) doesn't omit them. */
    get alreadyPurchasedAddonsTotal(): number {
        return this.alreadyPurchasedAddons.reduce((sum: number, a: any) => {
            const price = this.billingInterval === 'Yearly' ? this.toNumber(a.yearlyPrice) : this.toNumber(a.monthlyPrice);
            return sum + price;
        }, 0);
    }

    extraCostFor(item: LocationRegisterType): number {
        const extra = Math.max(0, item.quantity - (item.ownedQuantity ?? 0));
        if (!extra) return 0;
        const unitPrice = this.billingInterval === 'Yearly' ? item.price * 12 : item.price;
        return extra * unitPrice;
    }

    /** Full ongoing cost of extra units above the plan's included default — owned-before-this-
     *  session units (alreadyPurchasedExtra) plus any new ones added via the stepper right now.
     *  Unlike extraCostFor() (new units only — correct for "amount due today," which must never
     *  re-bill something already paid for), this is for projecting the account's real total
     *  going-forward bill, which must include everything they're already being charged for. */
    private fullExtraCostFor(item: LocationRegisterType): number {
        const defaultQty = item.id === 1 ? this.plan?.defaultRegister : this.plan?.defaultLocation;
        const totalExtra = Math.max(0, item.quantity - this.toNumber(defaultQty));
        if (!totalExtra) return 0;
        const unitPrice = this.billingInterval === 'Yearly' ? item.price * 12 : item.price;
        return totalExtra * unitPrice;
    }

    /** Extra units already owned before this checkout session — i.e. bought on a previous visit,
     *  distinct from extraCostFor()'s "new units being added right now" via the stepper. Returns 0
     *  (renders nothing) when the account owns no more than the plan's included default. */
    alreadyPurchasedExtra(item: LocationRegisterType): number {
        const defaultQty = item.id === 1 ? this.plan?.defaultRegister : this.plan?.defaultLocation;
        return Math.max(0, (item.ownedQuantity ?? 0) - this.toNumber(defaultQty));
    }

    /** Gates the Order Summary's "already on your account" guidance note — shown only when there's
     *  actually an already-purchased line for it to explain, so it doesn't clutter a first-time
     *  checkout with no prior purchases. */
    get hasAnyAlreadyPurchasedLine(): boolean {
        if (this.hardwareOnlyCheckout) {
            return false;
        }
        return this.alreadyPurchasedAddons.length > 0
            || this.registersAndLocationsList.some((item) => this.alreadyPurchasedExtra(item) > 0);
    }

    get registerLocationExtraTotal(): number {
        return this.roundCurrency(this.registersAndLocationsList.reduce((sum, item) => sum + this.extraCostFor(item), 0));
    }

    /** Full ongoing register/location extra cost (owned + new) — see fullExtraCostFor()'s doc
     *  comment. Used by recurringTotalDisplay's "future full bill" projection. */
    get fullRegisterLocationExtraTotal(): number {
        return this.roundCurrency(this.registersAndLocationsList.reduce((sum, item) => sum + this.fullExtraCostFor(item), 0));
    }

    /** Ongoing register/location extra cost for units already owned BEFORE this checkout session —
     *  i.e. alreadyPurchasedExtra(item) priced out. Unlike fullRegisterLocationExtraTotal (owned +
     *  new), this deliberately excludes new stepper additions — currentRecurringBaselineSubtotal's
     *  local fallback needs the account's real *current* bill, not one that already includes units
     *  not yet purchased, otherwise those new units get counted once here and again via
     *  registerLocationExtraTotal in recurringTotalDisplay's usage-only branch. */
    private get ownedRegisterLocationExtraTotal(): number {
        return this.roundCurrency(this.registersAndLocationsList.reduce((sum, item) => {
            const unitPrice = this.billingInterval === 'Yearly' ? item.price * 12 : item.price;
            return sum + this.alreadyPurchasedExtra(item) * unitPrice;
        }, 0));
    }

    get hardwareTotal(): number {
        return this.roundCurrency(this.selectedHardware.reduce((sum, item) => sum + (this.toNumber(item.price) * (item.selectedQuantity || 0)), 0));
    }

    /** Recurring per-period charge: plan + add-ons + extra registers/locations — what bills every
     *  {Monthly|Yearly} period going forward. Hardware is a one-time purchase, excluded here. This
     *  is the pre-tax subtotal — used for the Order Summary "Recurring total" row, which sits above
     *  its own separate "Sales tax" row, matching normal invoice convention. */
    get recurringTotal(): number {
        return this.roundCurrency(this.planPeriodPrice + this.addonsTotal + this.registerLocationExtraTotal);
    }

    /** "Recurring total" row shown in the Order Summary. Unlike recurringTotal (which excludes
     *  the plan fee during a usage-only checkout so today's charge stays add-ons-only), this
     *  always folds in planDisplayPrice so the row reflects the true combined monthly/yearly bill
     *  the account will be on going forward, not just what this particular order adds. Uses
     *  fullRegisterLocationExtraTotal (owned + new), not registerLocationExtraTotal (new-only) —
     *  otherwise a usage-only checkout that's only adding, say, 1 more location on top of 3
     *  already-purchased ones would project a bill missing those 3 already-billed extras. */
    get recurringTotalDisplay(): number {
        if (this.isUsageOnlyCheckout) {
            return this.roundCurrency(this.currentRecurringBaselineSubtotal + this.registerLocationExtraTotal + this.addonsTotal);
        }
        return this.roundCurrency(this.planDisplayPrice + this.fullRegisterLocationExtraTotal + this.addonsTotal + this.alreadyPurchasedAddonsTotal);
    }

    private get currentRecurringBaselineSubtotal(): number {
        const setup = this.currentBusinessSetup;
        const subscription = setup?.subscription ?? setup?.Subscription ?? {};
        const subscriptionBaseline = readSubscriptionRecurringSubtotal(subscription, this.taxRate, this.plan?.addons || []);
        const invoiceBaseline = readInvoiceRecurringSubtotal(this.currentInvoice);
        const localBaseline = this.planDisplayPrice + this.ownedRegisterLocationExtraTotal + this.alreadyPurchasedAddonsTotal;
        return this.roundCurrency(Math.max(subscriptionBaseline, invoiceBaseline, localBaseline));
    }

    /** Tax-inclusive version of recurringTotal — for copy that states what will actually be
     *  charged on a future date (the trial-end deferred bill), where a bare subtotal would
     *  understate the real charge rather than being a breakdown line next to its own tax row.
     *  Taxes the full recurring total including the plan fee — matches signup-v5/checkout
     *  .component.ts's getSubtotal/getGrandTotal, which taxes the whole subtotal. */
    get recurringTotalWithTax(): number {
        return this.roundCurrency(this.recurringTotal + this.calculateTax(this.recurringTotal));
    }

    /** Tax-inclusive version of recurringTotalDisplay — for the trial-end deferred-bill copy,
     *  which must match the combined plan + add-ons figure shown in the "Recurring total" row
     *  above it rather than recurringTotalWithTax's add-ons-only amount. Excludes the plan fee
     *  from tax — see nextBillingTaxAmount's doc comment for why. */
    get recurringTotalDisplayWithTax(): number {
        return this.roundCurrency(this.recurringTotalDisplay + this.nextBillingTaxAmount);
    }

    /** Tax portion of the next billing cycle's charge — shown next to "Sales tax" during a free
     *  trial, where taxAmount itself is $0 today (nothing is charged today), so the row would
     *  otherwise give no hint of what tax actually applies once billing starts.
     *
     *  Taxes the full recurring total including the plan fee — confirmed against the account
     *  owner's expected invoice total (a $212 recurring total — $35 plan + $79 locations + $49
     *  registers + $49 add-on — taxed as a whole at 8.25% = $17.49, matching submitOrder()'s
     *  per-line taxAmount on the plan's own subscriptionPlanPkg entry). */
    get nextBillingTaxAmount(): number {
        return this.calculateTax(this.recurringTotalDisplay);
    }

    /** Amount actually charged today. Business rule (confirmed): hardware is never deferred — it
     *  ships regardless of billing status, so it's always charged now. The recurring portion — plan
     *  fee, extra registers/locations, and add-ons alike — always defers to the next billing cycle
     *  (subscription.IsFreeTrial=true in confirm()'s payload signals this to the backend), not only
     *  while the account's own isFreeTrial flag happens to be set — that flag can be stale/false for
     *  an account that should still get deferred billing here. This applies the same whether it's a
     *  usage-only "Buy more" cart or a genuine plan pick/switch — completeUsageOnlyPurchase() already
     *  never puts add-ons through a Stripe charge for the usage-only path, and this makes the
     *  plan-switch path match rather than charging add-ons immediately there. */
    get subtotal(): number {
        return +this.hardwareTotal.toFixed(2);
    }

    /** Taxes the full subtotal including the plan fee — matches signup-v5/checkout.component.ts's
     *  getSubtotal/getGrandTotal, which taxes the whole subtotal with no plan-fee carve-out. */
    get taxAmount(): number {
        return this.calculateTax(this.subtotal);
    }

    /** "Amount due today" — also what's sent to Stripe as the immediate charge in confirm(), so
     *  this number and the actual charge never drift apart. Relies on the backend honoring
     *  subscription.IsFreeTrial to defer the plan/register-location portion's billing to the
     *  trial end date — verify that with backend/Stripe before relying on this in production. */
    get grandTotal(): number {
        return +(this.subtotal + this.taxAmount).toFixed(2);
    }

    /** Full ongoing value of the subscription — plan + add-ons + register/location extras +
     *  hardware — regardless of trial deferral. Unlike subtotal/taxAmount/grandTotal above (which
     *  are deliberately $0 for the recurring portion during a trial, since that's what's actually
     *  due today and charged to Stripe), this is what confirm() sends as
     *  totalCost/subTotalCost/TotalTaxAmount to FinishSignUpProcess/updateSubscriptionV3. The
     *  backend persists totalCost as BusinessSetup.TotalCost — sending the deferred $0 there
     *  permanently zeroes it out (ResolveBusinessAmount then returns 0 once a card is later
     *  attached), even though nothing about the subscription's real value was ever $0. Matches
     *  signup-v5/checkout.component.ts's getSubtotal/getGrandTotal, which never defer for a trial.
     *  Only reached for a non-usage-only submit — applyUsageOnlySubscriptionTotals() overrides
     *  these fields for usage-only checkouts using recurringTotalDisplay instead (see that
     *  method). Must mirror recurringTotalDisplay's non-usage-only formula (fullRegisterLocation-
     *  ExtraTotal + alreadyPurchasedAddonsTotal, not the new-only registerLocationExtraTotal)
     *  so a plan-switch account that already owns extra registers/locations or add-ons doesn't
     *  send the backend a totalCost lower than what the Order Summary just displayed. */
    get fullSubscriptionSubtotal(): number {
        return +(this.planDisplayPrice + this.addonsTotal + this.alreadyPurchasedAddonsTotal + this.fullRegisterLocationExtraTotal + this.hardwareTotal).toFixed(2);
    }

    /** Taxes the full subtotal including the plan fee — matches signup-v5/checkout.component.ts's
     *  getSubtotal/getGrandTotal. This is what confirm() sends as TotalTaxAmount to
     *  FinishSignUpProcess/updateSubscriptionV3 (see fullSubscriptionSubtotal's doc comment), so
     *  getting this wrong doesn't just misdisplay — it persists a wrong tax figure to the backend
     *  that then drifts from Stripe's real invoice. */
    get fullSubscriptionTax(): number {
        return this.calculateTax(this.fullSubscriptionSubtotal);
    }

    get fullSubscriptionGrandTotal(): number {
        return +(this.fullSubscriptionSubtotal + this.fullSubscriptionTax).toFixed(2);
    }

    // --- Coupon ---
    private buildCouponPayload(code: string): CouponValidationRequest {
        return {
            couponId: '',
            couponCode: code,
            planId: this.plan?.cosmosId || '',
            currentSubscriptionPrice: this.recurringTotal.toString(),
            locationId: this.plan?.cosmosId || '',
            customerId: this._global.userInfo?.userID || '',
            billingIntrval: this.billingInterval,
        };
    }

    applyCoupon(): void {
        const code = this.couponCode?.trim();
        if (!code || this.isCouponValidating) return;

        if (this.couponSuccess && this.appliedCouponCode) {
            this.clearCoupon();
            this.couponCode = code;
        }

        this.isCouponValidating = true;
        this.couponError = false;
        this.validationMessage = `Validating coupon ${code}…`;

        const businessName = this.currentBusinessSetup?.businessName || this._global.userInfo?.companyName || '';

        this._discountService.validateCoupon(this.buildCouponPayload(code), businessName)
            .pipe(timeout(8000), catchError(() => of({ success: false })), finalize(() => this.isCouponValidating = false))
            .subscribe((res: any) => {
                if (!res?.success || !res.data) {
                    this.couponError = true;
                    this.couponSuccess = false;
                    this.validationMessage = "Sorry, this coupon can't be applied.";
                    return;
                }

                const data = res.data;
                const category = (data.subscriptionDiscountCategory || 'plan').trim().toLowerCase();

                if (category === 'addon') {
                    const selectedAddons = this.optionalAddons.filter((a: any) => a.selected);
                    if (!selectedAddons.length) {
                        this.couponError = true;
                        this.validationMessage = 'This coupon applies to an add-on — select an add-on first, then apply it again.';
                        return;
                    }
                    selectedAddons.forEach((a: any) => {
                        a.discountApplied = true;
                        a.discountedPrice = data.discountedPrice;
                    });
                } else {
                    this.plan.discountApplied = true;
                    this.plan.discountedPrice = data.discountedPrice;
                    this.plan.originalPrice = data.originalPrice;
                }

                this.lastCouponCategory = category;
                this.stripeCouponId = data.stripeCouponId || '';
                this.stripeProductKey = data.stripeProductKey || '';
                this.appliedDiscountDetail = data;
                this.appliedCouponCode = code;
                this.discountedPrice = this.toNumber(data.discountedPrice);
                this.discountAmount = this.toNumber(data.discountAmount ?? (data.originalPrice - data.discountedPrice));
                this.couponSuccess = true;
                this.couponError = false;
                this.validationMessage = `Coupon ${code} applied.`;
            });
    }

    clearCoupon(): void {
        if (this.plan) {
            this.plan.discountApplied = false;
            this.plan.discountedPrice = 0;
        }
        this.optionalAddons.forEach((a: any) => {
            a.discountApplied = false;
            a.discountedPrice = 0;
        });

        const userId = this._global.userInfo?.userID;
        if (this.appliedCouponCode && userId) {
            const payload: RemoveCouponRequest = { couponCode: this.appliedCouponCode, userId };
            this._discountService.removeAppliedCoupon(payload).pipe(timeout(8000), catchError(() => of({ success: false }))).subscribe();
        }

        this.couponCode = '';
        this.appliedCouponCode = null;
        this.appliedDiscountDetail = null;
        this.couponSuccess = false;
        this.couponError = false;
        this.validationMessage = '';
        this.lastCouponCategory = null;
        this.discountedPrice = 0;
        this.discountAmount = 0;
    }

    // --- Submit ---
    get canSubmit(): boolean {
        if (this.isUsageOnlyCheckout && !this.hasAnyUsageSelection) return false;
        if (this.selectedHardware.length && !this.shippingAddressValid) return false;
        if (this.selectedHardware.length && !this.hasBusinessAddress && !this.canSaveBusinessAddress) return false;
        return this.terms && !this.isSubmitting && !this.isLoading;
    }

    get submitTitle(): string {
        if (this.isLoading) return 'Loading plan details…';
        if (this.isUsageOnlyCheckout && !this.hasAnyUsageSelection) {
            return this.hardwareOnlyCheckout ? 'Add at least one item before confirming' : 'Add a register, location or add-on before confirming';
        }
        if (this.selectedHardware.length && !this.hasBusinessAddress && !this.canSaveBusinessAddress) return 'Add your business address before confirming — it\'s needed to calculate sales tax';
        if (this.selectedHardware.length && !this.shippingAddressValid) return 'Enter a complete shipping address for your hardware order';
        if (!this.terms) return 'Please accept the terms and privacy policy to continue';
        return '';
    }

    get submitLabel(): string {
        if (this.isSubmitting) return 'Confirming…';
        return 'Checkout';
    }

    confirm(): void {
        if (!this.canSubmit) return;

        // Hardware is always charged immediately (chargeHardware()/purchaseHardware — never through
        // Stripe Checkout's own card-collection page, unlike a first-time plan purchase), so a
        // missing card on file would be rejected by the backend either way (see submitOrder()'s
        // card/payment-method fallback). Catch it here instead of round-tripping through
        // updateSubscriptionV3 and purchaseHardware first with requests that are guaranteed to 400 —
        // same end state (showAddCardStep with "Add a card before purchasing hardware"), no wasted
        // failing network calls first.
        if (this.selectedHardware.length && !this._businessSetupProgress.hasCardOnFile) {
            this.awaitingCardForPrecheck = true;
            this.showAddCardStep = true;
            return;
        }

        this.isSubmitting = true;
        this._spinner.show();

        // No standalone "Save business address" step anymore — the inline form (when it's the one
        // in play, i.e. the account has no address on file yet) saves as the first step of
        // confirming, not before it, so canSubmit only needed the form to be valid, not saved.
        if (this.selectedHardware.length && !this.hasBusinessAddress) {
            this.saveBusinessAddressForConfirm().subscribe((success) => {
                if (!success) {
                    this.isSubmitting = false;
                    this._spinner.hide();
                    this._connMessageService.showToastMessage('Unable to save business address. Please try again.', 'error');
                    return;
                }
                this.submitOrder();
            });
            return;
        }

        this.submitOrder();
    }

    private submitOrder(): void {
        const setup = this.currentBusinessSetup;
        const websiteDomain = window.location.origin;

        const subscriptionPlanPkg: any[] = [];

        // Taxed the same as every other line — confirmed against the account owner's expected
        // invoice total: $35 plan + $79 locations + $49 registers + $49 add-on = $212, taxed as a
        // whole at 8.25% = $17.49 (not $14.60, which is what taxing only the $177 extras/add-ons
        // portion — carving the plan fee out — produced).
        const planLineTax = this.calculateTax(this.planPeriodPrice);
        subscriptionPlanPkg.push({
            // cosmosId first, not planId: planId can fall back to the local plan-list.json catalog's
            // small internal id (1/2/3/4), which collides with LocationRegisterList's reserved ids
            // (1=Registers, 2=Locations) — a Plus-plan (catalogMatch.id===2) purchase then stored its
            // own $35 line under packageId "2", and readSubscriptionRecurringExtraQuantity's Locations
            // lookup (subscription-total.ts) misread that plan line as "1 extra location purchased."
            packageId: this.plan?.cosmosId ?? this.plan?.planId,
            name: this.plan?.planName,
            qty: 1,
            pricePerPackage: this.planPeriodPrice.toString(),
            subTotalCost: this.planPeriodPrice,
            taxAmount: planLineTax.toString(),
            isDiscountPercent: false,
            isTaxPercent: true,
            totalPrice: (this.planPeriodPrice + planLineTax).toFixed(2),
            IsSubscription: true,
            stripeProductKey: this.resolveStripeProductKey(this.plan),
            is_discount_applied: !!this.plan?.discountApplied,
            discount_amount: this.plan?.discountApplied ? this.discountAmount : 0,
            discounted_price: this.plan?.discountApplied ? this.discountedPrice : 0,
            coupon_code: this.plan?.discountApplied ? this.appliedCouponCode : null,
        });

        this.registersAndLocationsList.forEach((item) => {
            // Reference the account's actual owned count, not the plan's static default — see
            // resolveOwnedQuantity()'s doc comment. Using the default here would re-bill/re-count
            // units the merchant already owns whenever it's lower than what they actually have.
            const extraQty = Math.max(0, item.quantity - (item.ownedQuantity ?? 0));
            if (!extraQty) return;
            const unitPrice = this.billingInterval === 'Yearly' ? item.price * 12 : item.price;
            const lineTotal = extraQty * unitPrice;
            const lineTax = this.calculateTax(lineTotal);
            subscriptionPlanPkg.push({
                packageId: item.id,
                name: item.title,
                qty: extraQty,
                pricePerPackage: unitPrice.toString(),
                subTotalCost: lineTotal,
                taxAmount: lineTax.toString(),
                isDiscountPercent: false,
                isTaxPercent: true,
                totalPrice: (lineTotal + lineTax).toFixed(2),
                IsLocationRegister: true,
                stripeProductKey: this.resolveStripeProductKey(item),
            });
        });

        this.optionalAddons.filter((a: any) => a.selected).forEach((addon: any) => {
            const price = (this.lastCouponCategory === 'addon' && addon.discountApplied)
                ? this.toNumber(addon.discountedPrice)
                : (this.billingInterval === 'Yearly' ? this.toNumber(addon.yearlyPrice) : this.toNumber(addon.monthlyPrice));
            const lineTax = this.calculateTax(price);
            subscriptionPlanPkg.push({
                packageId: addon.id,
                name: addon.planName,
                qty: 1,
                pricePerPackage: price.toString(),
                subTotalCost: price,
                taxAmount: lineTax.toString(),
                isDiscountPercent: false,
                isTaxPercent: true,
                totalPrice: (price + lineTax).toFixed(2),
                IsAddons: true,
                stripeProductKey: this.resolveStripeProductKey(addon),
                is_discount_applied: !!addon.discountApplied,
                discount_amount: addon.discountApplied ? this.discountAmount : 0,
                discounted_price: addon.discountApplied ? addon.discountedPrice : 0,
                coupon_code: addon.discountApplied ? this.appliedCouponCode : null,
            });
        });

        this.selectedHardware.forEach((item) => {
            const lineTotal = this.toNumber(item.price) * (item.selectedQuantity || 0);
            const lineTax = this.calculateTax(lineTotal);
            subscriptionPlanPkg.push({
                packageId: item.id,
                name: item.title,
                qty: item.selectedQuantity,
                pricePerPackage: item.price?.toString(),
                subTotalCost: lineTotal,
                taxAmount: lineTax.toString(),
                isDiscountPercent: false,
                isTaxPercent: true,
                totalPrice: (lineTotal + lineTax).toFixed(2),
                IsHardware: true,
            });
        });

        const TransactionDetails = {
            accountType: '',
            accountNumber: '',
            transId: '',
            responseCode: '1',
            paymentMethod: 'Stripe',
            authorization: '',
            merchantName: 'Connect-360',
            billTo: (this.selectedHardware.length && !this.isBusinessAddressShipping)
                ? {
                    phoneNumber: setup?.phone,
                    firstName: '',
                    lastName: '',
                    address: this.shippingForm.value.address,
                    address2: this.shippingForm.value.address2,
                    city: this.shippingForm.value.city,
                    state: this.selectedShippingState?.abbreviation,
                    zip: this.shippingForm.value.zipCode,
                    country: 'US',
                }
                : {
                    phoneNumber: setup?.phone,
                    firstName: '',
                    lastName: '',
                    address: setup?.address,
                    city: setup?.city,
                    state: setup?.state,
                    zip: setup?.zip,
                    country: 'US',
                },
            totalAmount: this.grandTotal,
            dateTime: new Date(),
        };

        const CCCustomerProfile = {
            TransactionId: '',
            StartDate: formatDate(new Date(), 'yyyy-MM-dd', 'en-US'),
            IntervalLength: this.billingInterval === 'Monthly' ? 1 : 12,
            Amount: this.grandTotal.toFixed(2),
            TrialAmount: 0,
            SubTotal: this.subtotal,
            Tax: this.taxAmount.toFixed(2),
        };

        const data: any = {
            businessId: setup?.businessId,
            NoOfLocations: this.registersAndLocationsList.find((i) => i.id === 2)?.quantity ?? this.plan?.defaultLocation,
            // PerNoOfRegister is the target absolute register count per v3/subscription/update's
            // contract — must match NoOfRegisters below, not a hardcoded 1 (which was pinning
            // every account's register count back down to 1 on every submit).
            PerNoOfRegister: this.registersAndLocationsList.find((i) => i.id === 1)?.quantity ?? this.plan?.defaultRegister,
            NoOfRegisters: this.registersAndLocationsList.find((i) => i.id === 1)?.quantity ?? this.plan?.defaultRegister,
            userID: setup?.userID,
            SaveInfo: true,
            subscription: {
                TransactionDetails,
                hardwarePkgId: this.selectedHardware.map((h) => h.id),
                totalHardwarePkgCost: this.hardwareTotal.toFixed(2),
                totalSupportPkgCost: '0',
                planCost: this.planPeriodPrice.toString(),
                planId: this.plan?.cosmosId,
                // Tells the backend to defer the plan/register-location/add-on portion's billing to
                // the next billing cycle instead of charging it in this transaction — always true
                // now, not gated on the account's own isFreeTrial flag, to match subtotal/grandTotal
                // above always charging hardware-only today (see subtotal's doc comment for why).
                IsFreeTrial: true,
                // Deliberately NOT grandTotal/taxAmount/subtotal — those are "amount due today"
                // (correctly $0 during a trial). totalCost/subTotalCost/TotalTaxAmount are what the
                // backend persists as the subscription's actual ongoing value — see
                // fullSubscriptionSubtotal's doc comment.
                totalCost: this.fullSubscriptionGrandTotal.toFixed(2),
                subscriptionPlanPkg,
                WooCommercePlanPkg: [],
                DomainPlanPkg: [],
                hardwarePkg: [],
                TotalTaxAmount: this.fullSubscriptionTax.toFixed(2),
                subTotalCost: this.fullSubscriptionSubtotal,
                TotalDiscountAmount: this.discountAmount,
                TotalTaxValue: this.taxRate,
                TotalDiscountValue: 0,
                IsTotalTaxPercente: true,
                IsTotalDiscountPercent: false,
                SubscribedFeatures: [],
            },
            PackageName: this.plan?.planName,
            CCCustomerProfile,
            // Required alongside CouponCode for the discount to actually apply server-side — an
            // empty object here silently no-ops the coupon despite it showing as applied in the UI.
            DiscountDet: this.appliedDiscountDetail ? {
                Id: this.appliedDiscountDetail.id,
                DiscountName: this.appliedDiscountDetail.discountName,
                CouponCodes: this.appliedCouponCode,
                DiscountValueType: this.appliedDiscountDetail.discountValueType,
                DiscountValue: this.appliedDiscountDetail.discountValue,
                StripeCouponId: this.appliedDiscountDetail.stripeCouponId,
            } : {},
            CouponCode: this.appliedCouponCode || null,
            SuccessUrl: `${websiteDomain}/#/app/billing-and-plans`,
            FailureUrl: `${websiteDomain}/#/check-out`,
        };
        this.applyUsageOnlySubscriptionTotals(data);

        localStorage.setItem('selectedPlanDetails', JSON.stringify(this.plan));
        this.lastConfirmPayload = data;

        // Always record first, even when nothing is due today (trial + usage-only + no hardware) —
        // "nothing charged today" only means the amount is deferred to the trial-end bill, not that
        // there's nothing to record. Skipping this call here used to mean the register/location/
        // add-on purchase was never persisted server-side at all: the only network call that fired
        // was Stripe's setup-intent from the optional add-card step, which saves a card but records
        // nothing about what was purchased.
        // Usage-only (Buy more) purchases and plan-switch purchases (an already-subscribed account
        // changing plans) both update an *existing* subscription, so they go through the dedicated
        // updateSubscriptionV3() endpoint rather than FinishSignUpProcess (which is for establishing
        // a brand-new subscription — used below for the true first-time-signup path — and is what
        // TOLERATE_FINISH_SIGNUP_FAILURE_FOR_USAGE_ONLY above was working around).
        const updatesExistingSubscription = this.isUsageOnlyCheckout || this.isSwitchingPlan;
        const recordPurchase$ = updatesExistingSubscription
            ? this._signup.updateSubscriptionV3(data)
            : this._signup.saveUserSignupInfo(data);

        recordPurchase$.pipe(retry(3)).subscribe({
            next: (res: any) => {
                // updateSubscriptionV3's success contract is just `success` — the responseStatusCode
                // 903 sentinel is specific to FinishSignUpProcess (the true first-time-signup path below).
                const succeeded = updatesExistingSubscription
                    ? !!res?.success
                    : (!!res?.success && res.responseStatusCode == 903);
                if (!succeeded) {
                    // The backend rejects hardware/usage purchases outright when the account has no
                    // Stripe payment method on file (e.g. "No payment method on file. Add a card
                    // before purchasing hardware.", responseStatusCode 900) — that's not a generic
                    // failure to swallow via the TOLERATE branch below, it's an actionable state the
                    // merchant can fix immediately. Route straight to the existing add-card step
                    // (same UI chargeHardware() falls back to) instead of showing the raw error.
                    // Don't check hasCardOnFile; the backend error is proof the card is needed.
                    if (this.isUsageOnlyCheckout && /card|payment method/i.test(String(res?.message || ''))) {
                        this.isSubmitting = false;
                        this._spinner.hide();
                        this.lastConfirmPayload = data;
                        this.showAddCardStep = true;
                        return;
                    }
                    // TODO: remove once FinishSignUpProcess failures for usage-only purchases are
                    // resolved on the backend. For now, don't block the merchant from adding a card
                    // (or seeing the success receipt) just because this recording call failed —
                    // scoped to usage-only only, not the full plan-purchase Stripe flow below, where
                    // proceeding past a recording failure ahead of an actual charge is much riskier.
                    if (this.TOLERATE_FINISH_SIGNUP_FAILURE_FOR_USAGE_ONLY && this.isUsageOnlyCheckout) {
                        this.completeUsageOnlyPurchase(data, subscriptionPlanPkg);
                        return;
                    }
                    this.isSubmitting = false;
                    this._spinner.hide();
                    this._connMessageService.showToastMessage(res?.message || 'Unable to process checkout. Please try again.', 'error');
                    return;
                }

                if (updatesExistingSubscription) {
                    // updateSubscriptionV3 just changed the account's plan/register/location counts
                    // and add-on flags server-side, but GlobalService.userInfo (what billing-and-plans's
                    // subscriptionAddonsList/registerCount/locationCount all read directly) is still
                    // the pre-purchase snapshot from login — without this, the merchant lands back on
                    // Subscription Management seeing the old plan/counts until their next full login.
                    // Fire-and-forget: doesn't block the receipt.
                    this.refreshUserInfo();
                    // The plan-switch line itself belongs on the receipt (that's what changed this
                    // session); usage-only purchases keep it filtered out since the account was
                    // already on that plan.
                    this.completeUsageOnlyPurchase(data, subscriptionPlanPkg, this.isSwitchingPlan);
                    return;
                }

                this._signup.saveUserSignupInfoForStripe(data).pipe(retry(3)).subscribe({
                    next: (stripeRes: any) => {
                        this.redirectToCheckout({ publicKey: stripeRes.publicKey, sessionId: stripeRes.sessionId });
                    },
                    error: () => {
                        this.isSubmitting = false;
                        this._spinner.hide();
                        this._connMessageService.showToastMessage('Unable to start payment. Please try again.', 'error');
                    },
                });
            },
            error: (error) => {
                if (this.TOLERATE_FINISH_SIGNUP_FAILURE_FOR_USAGE_ONLY && this.isUsageOnlyCheckout) {
                    this.completeUsageOnlyPurchase(data, subscriptionPlanPkg);
                    return;
                }
                this.isSubmitting = false;
                this._spinner.hide();
                this._connMessageService.showToastMessage(error?.message || 'Unable to process checkout. Please try again.', 'error');
            },
        });
    }

    /** Usage-only checkout (Buy more/Add) never pays through a Stripe Checkout redirect for
     *  registers/locations/add-ons — those already recorded above via saveUserSignupInfo() and
     *  either defer to the trial-end bill (IsFreeTrial: true, same mechanism the plan fee itself
     *  already used) or, for an already-paying account, bill the card on file the same way
     *  billing-and-plans's upgradePlan()/downgradePlan() do outside this page. Hardware is the one
     *  exception — it's a one-time physical purchase that always needs paying for now, so it's the
     *  only thing that ever triggers an actual Stripe Checkout redirect from this flow, scoped to
     *  just the hardware line items/total so the deferred portion is never double-charged. */
    /** Refreshes GlobalService.userInfo from B2CGraphClientApi (GETUSERBYUSERNAME) — same pattern
     *  general.component.ts uses after saving a time zone — so every consumer of businessSetup[0]
     *  (register/location counts, add-on feature flags) reflects what updateSubscriptionV3() just
     *  changed, instead of the snapshot from this session's login. */
    private refreshUserInfo(): void {
        const username = GlobalService.userInfo?.username
            ?? GlobalService.userInfo?.userName
            ?? GlobalService.userInfo?.Username;
        if (!username) return;
        this.isUserInfoRefreshed = false;
        this._userService.getUserByUserName(username).subscribe({
            next: (userRes: any) => {
                if (userRes?.success && userRes?.data) {
                    GlobalService.userInfo = userRes.data;
                    const setup = readAvailableBusinessSetup(userRes.data, null);
                    if (setup) {
                        try {
                            sessionStorage.setItem('business-Info', JSON.stringify(setup));
                        } catch { /* sessionStorage can be unavailable in private browsing */ }
                    }
                }
            },
            error: () => {
                // Keep showing the receipt even if the refresh fails, but still unblock
                // "Back to Subscription Management" — an HTTP error terminates the observable
                // without calling complete(), so isUserInfoRefreshed must be flipped here too or
                // the button (and, for the checkout flow, the Order Summary) stays stuck forever.
                this.isUserInfoRefreshed = true;
                this.tryClearLoading();
            },
            complete: () => {
                this.syncRegisterLocationOwnedBaselines();
                this.isUserInfoRefreshed = true;
                this.tryClearLoading();
            },
        });
    }

    /** Only reveal the Order Summary once every input that grandTotal/recurringTotalDisplay
     *  depends on has settled — see isUserInfoRefreshed's doc comment for why resolvePlan()
     *  finishing alone isn't enough. */
    private tryClearLoading(): void {
        if (this.isUserInfoRefreshed && this.isInvoicePreviewLoaded) {
            this.isLoading = false;
        }
    }

    private loadCurrentInvoicePreview(): void {
        this._subscriptionPlanService.getBusinessInvoice()
            .pipe(
                timeout(10000),
                catchError(() => of(null)),
            )
            .subscribe((res: any) => {
                this.currentInvoice = res?.data?.invoice ?? res?.Data?.Invoice ?? null;
                if (!this.currentInvoice || !this.registersAndLocationsList.length) {
                    this.syncRegisterLocationOwnedBaselines();
                    this.isInvoicePreviewLoaded = true;
                    this.tryClearLoading();
                    return;
                }

                this.syncRegisterLocationOwnedBaselines();
                this.persistCartState();
                this.isInvoicePreviewLoaded = true;
                this.tryClearLoading();
            });
    }

    private syncRegisterLocationOwnedBaselines(): void {
        if (!this.plan || !this.registersAndLocationsList.length) return;

        this.registersAndLocationsList.forEach((item) => {
            const defaultQty = item.id === 1 ? this.plan?.defaultRegister : this.plan?.defaultLocation;
            const previousOwnedQty = this.toNumber(item.ownedQuantity);
            const addedQty = Math.max(0, this.toNumber(item.quantity) - previousOwnedQty);
            const ownedQty = this.resolveOwnedQuantity(item.id as 1 | 2, this.toNumber(defaultQty));
            if (ownedQty <= 0) return;

            item.ownedQuantity = ownedQty;
            item.quantity = ownedQty + addedQty;
            item.selected = addedQty > 0;
        });

        this.persistCartState();
    }

    private completeUsageOnlyPurchase(data: any, subscriptionPlanPkg: any[], includePlanLine = false): void {
        this.purchasedLineItems = includePlanLine
            ? subscriptionPlanPkg
            : subscriptionPlanPkg.filter((line) => !line.IsSubscription);

        if (this.hardwareTotal > 0) {
            this.lastConfirmPayload = data;
            this.chargeHardware(true);
            return;
        }

        // No hardware — the registers/locations/add-ons portion recorded above is the whole
        // purchase, with no further Stripe redirect to round-trip through, so it's safe to drop the
        // cart now rather than deferring to a restore step that will never run.
        this.clearPersistedCartState();
        this.isSubmitting = false;
        this._spinner.hide();

        if (this.isFreeTrialAccount && !this._businessSetupProgress.hasCardOnFile) {
            this.showAddCardStep = true;
            return;
        }

        this.markSubmitSucceeded();
    }

    // --- One-time checkout lock: once a purchase actually completes, the browser Back button
    // must not be able to reopen /change-plan or /check-out and let the merchant buy again — those
    // routes stay in browser history even though there's nothing left to do there. Set here (every
    // success path) and only cleared by a deliberate re-entry point (billing-and-plans/topbar's own
    // navigation into /change-plan), never just by landing back on this page.
    static readonly CHECKOUT_LOCK_KEY = 'checkout-just-completed';

    /** True for any "Buy more"/"Add"/hardware-only trip (registers, locations, add-ons, hardware)
     *  — these are meant to be repeatable as many times as the merchant wants, unlike a full plan
     *  pick/switch, so the checkout lock must never apply to them. Checked both from live state
     *  (this.isUsageOnlyCheckout, once resolvePlan() has run) and from the raw entry signals
     *  (sessionStorage's one-shot usage intent, or a persisted usage-only cart) so it also reads
     *  correctly during ngOnInit's lock check, which runs before resolvePlan(). */
    private isUsageOnlyEntry(): boolean {
        if (this.isUsageOnlyCheckout) return true;
        try {
            if (sessionStorage.getItem('checkout-usage-intent')) return true;
            const raw = localStorage.getItem('checkout-cart-state');
            if (raw && JSON.parse(raw)?.isUsageOnlyCheckout) return true;
        } catch { /* ignore */ }
        return false;
    }

    private markSubmitSucceeded(): void {
        this.submitSucceeded = true;
        // Only a real plan pick/switch is locked from Back-button reuse — hardware/add-on/
        // register/location purchases stay freely repeatable, per explicit product decision.
        if (this.isUsageOnlyEntry()) return;
        try { sessionStorage.setItem(PaymentPageComponent.CHECKOUT_LOCK_KEY, 'true'); } catch { /* ignore */ }
    }

    /** Charges the selected hardware directly against the card on file via
     *  POST v3/subscription/hardware — synchronous, no Stripe Checkout redirect/round-trip.
     *  allowAddCardRetry is true only on the first attempt (from completeUsageOnlyPurchase()): if the
     *  account has no card on file yet, the endpoint's "Add a card before purchasing hardware" error
     *  sends the merchant through the add-card step, and onAddCardStepSaved() calls back in here
     *  with allowAddCardRetry false so a second missing-card failure surfaces as a plain error
     *  instead of looping back into the add-card step. */
    private chargeHardware(allowAddCardRetry: boolean): void {
        this.isSubmitting = true;
        this._spinner.show();
        const setup = this.currentBusinessSetup;
        const hardwarePayload = {
            UserID: setup?.userID,
            BusinessId: setup?.businessId,
            Items: this.selectedHardware.map((h) => ({ PackageId: h.id, Qty: h.selectedQuantity })),
            TaxAmount: this.calculateTax(this.hardwareTotal),
        };
        this._signup.purchaseHardware(hardwarePayload).pipe(retry(3)).subscribe({
            next: (res: any) => {
                if (!res?.success) {
                    this.isSubmitting = false;
                    this._spinner.hide();
                    // Backend explicitly says no payment method — show add-card popup immediately.
                    // Don't rely on hasCardOnFile (client cache may be stale); trust the backend's
                    // rejection as proof a card is needed.
                    if (allowAddCardRetry && /card|payment method/i.test(String(res?.message || ''))) {
                        this.showAddCardStep = true;
                        return;
                    }
                    this._connMessageService.showToastMessage(res?.message || 'Unable to process the hardware purchase. Please try again from Subscription Management.', 'error');
                    return;
                }
                this.finishHardwarePurchase(res.data);
            },
            error: () => {
                this.isSubmitting = false;
                this._spinner.hide();
                this._connMessageService.showToastMessage('Unable to process the hardware purchase. Please try again from Subscription Management.', 'error');
            },
        });
    }

    /** Common tail for a hardware purchase that actually went through purchaseHardware() —
     *  mirrors the no-hardware tail at the bottom of completeUsageOnlyPurchase(). */
    private finishHardwarePurchase(responseData: any): void {
        // Prefer the endpoint's own priced/titled line items when present so the receipt reflects
        // exactly what was charged; fall back to what was already built locally if the response
        // shape is missing them.
        const chargedItems = Array.isArray(responseData?.items) ? responseData.items : null;
        if (chargedItems) {
            this.purchasedLineItems = [
                ...this.purchasedLineItems.filter((line) => !line.IsHardware),
                ...chargedItems.map((item: any) => {
                    const lineTax = this.calculateTax(item.lineTotal);
                    return {
                        packageId: item.id,
                        name: item.title,
                        qty: item.qty,
                        pricePerPackage: item.unitPrice,
                        subTotalCost: item.lineTotal,
                        taxAmount: lineTax,
                        totalPrice: +(item.lineTotal + lineTax).toFixed(2),
                        IsHardware: true,
                    };
                }),
            ];
        }
        this.clearPersistedCartState();
        this.isSubmitting = false;
        this._spinner.hide();
        this.markSubmitSucceeded();
    }

    onAddCardStepSaved(event: StripeCardSavedEvent): void {
        this._businessSetupProgress.applyCardOnFile(event);
        this.showAddCardStep = false;

        // confirm()'s pre-check sent us here before submitOrder() ever ran — there's no
        // lastConfirmPayload/purchase attempt to resume, so just re-run confirm() from scratch now
        // that hasCardOnFile is true.
        if (this.awaitingCardForPrecheck) {
            this.awaitingCardForPrecheck = false;
            this.confirm();
            return;
        }

        // A card missing on file is exactly why purchaseHardware() rejected the purchase (see
        // chargeHardware()) — now that one's saved, retry the charge instead of just showing a
        // receipt for a purchase that never actually happened.
        if (this.hardwareTotal > 0 && this.lastConfirmPayload) {
            this.chargeHardware(false);
            return;
        }

        this.markSubmitSucceeded();
        this.resyncSubscriptionAfterCardAdded();
    }

    /** completeUsageOnlyPurchase() already recorded this purchase via updateSubscriptionV3 before
     *  showAddCardStep ever rendered — the account had no Stripe customer/subscription attached at
     *  that point, so the backend's CustomerId/SubscriptionId-recovery fallback (List Subscriptions
     *  for the customer) had nothing to find. Now that a card is on file, re-trigger the same
     *  endpoint so that fallback runs again with a real Stripe customer/payment method in place.
     *  Re-sends the business/plan identifiers and the current totals (via fullSubscription* — never
     *  the trial-deferred subtotal/grandTotal, see their doc comments), but NOT subscriptionPlanPkg
     *  — those line items already represent the purchase that's already been recorded; resending
     *  them here would read as buying them a second time. Fire-and-forget: doesn't block the
     *  already-shown receipt. */
    private resyncSubscriptionAfterCardAdded(): void {
        if (!this.lastConfirmPayload) return;
        const resyncPayload = {
            ...this.lastConfirmPayload,
            subscription: {
                ...this.lastConfirmPayload.subscription,
                subscriptionPlanPkg: [],
                totalCost: this.lastConfirmPayload.subscription.totalCost,
                subTotalCost: this.lastConfirmPayload.subscription.subTotalCost,
                TotalTaxAmount: this.lastConfirmPayload.subscription.TotalTaxAmount,
            },
        };
        this._signup.updateSubscriptionV3(resyncPayload).subscribe({
            next: () => { },
            error: () => { /* best-effort resync; card is already saved either way */ },
        });
    }

    skipAddCardStep(): void {
        this.showAddCardStep = false;
        this.markSubmitSucceeded();
    }

    get purchasedLineItemsSubtotal(): number {
        return buildUsageReceiptTotals(this.purchasedLineItems).subtotal;
    }

    get purchasedLineItemsTax(): number {
        return buildUsageReceiptTotals(this.purchasedLineItems).tax;
    }

    get purchasedLineItemsTotal(): number {
        return buildUsageReceiptTotals(this.purchasedLineItems).total;
    }

    private resolveStripeProductKey(item: any): string {
        if (!item) return '';
        const sandboxKey = item.StripeSandboxProductKey || '';
        const prodKey = item.StripeProductKey || '';
        return environment.production ? (prodKey || sandboxKey) : (sandboxKey || prodKey);
    }

    /** Only ever called for a brand-new plan purchase now — usage-only hardware purchases go
     *  through purchaseHardware() (a direct, synchronous charge to the card on file) instead of a
     *  Stripe Checkout redirect, so there's no in-flight cart to preserve across a round trip here. */
    private redirectToCheckout(session: any): void {
        this.clearPersistedCartState();
        localStorage.setItem('goto', window.location.origin + '/#/app/billing-and-plans');
        const stripe = Stripe(session.publicKey);
        stripe?.redirectToCheckout({ sessionId: session.sessionId });
    }
}
